import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ContextSelectorBar from '../components/ContextSelectorBar'
import CurrentContextBar from '../components/CurrentContextBar'
import GradientButton from '../components/GradientButton'
import IconButton from '../components/IconButton'
import KPICard from '../components/KPICard'
import type { MatrixData } from '../components/MatrixTable'
import { DatabaseRow as BaseDatabaseRow } from '../utils/feedCalculations'
import { api } from '../utils/api'
import { writeCurrentFarm } from '../utils/farmWizard'
import { ensureOfflineReady } from '../offline/initOffline'
import { dexieFarmRepo } from '../offline/repos/DexieFarmRepo'
import { dexieLotRepo } from '../offline/repos/DexieLotRepo'
import { dexieTaskRepo } from '../offline/repos/DexieTaskRepo'
import { ACTIVITY_EVENTS_UPDATED, dexieActivityRepo } from '../offline/repos/DexieActivityRepo'
import { loadDashboardSelection, persistDashboardSelection } from '../utils/dashboardSelection'
import {
  buildDashboardLotSelectionTokens,
  findLotByDashboardSelection,
  matchesDashboardLotSelection,
  normalizeDashboardLotSelection
} from '../utils/dashboardLotSelection'
import { resolvePersistedDashboardLotSelection } from './dashboard/persistedLotSelection'
import { loadDashboardWaterRows } from '../utils/dashboardWater'
import {
  collectDashboardWaterBaseKeys,
  computeActiveDailyFeedKpi,
  computeAvgWaterMetricKpi,
  computeAvgWaterTempKpi,
  computeFishStockKpi,
  computeGrowthRateKpi,
  computeMortalityKpi,
  type FeedDebugRow,
  type FeedDebugSummary,
  type FishStockDebugInfo,
  getWeekKey as getWeekKeyFromKpis,
  getWeekKeyInTimeZone
} from '../utils/dashboardKpis'
import { getTimeZoneFromFarm, resolveTimeZone } from '../utils/timeBucketing'
import { classifyTaskStatus, summarizeTasksForDashboard } from '../utils/taskApi'
import type { BiomassDataRow } from '../utils/productionUnitTable'
import { dexieProductionTableRepo } from '../offline/repos/DexieProductionTableRepo'
import { buildDashboardReportPdf } from '../utils/dashboardReportPdf'
import { feedLogApi } from '../utils/feedLogApi'
import { mortalityLogApi } from '../utils/mortalityLogApi'
import { overlayMortalityLogsOnProductionRows } from './biomass/mortalityLogBridge'
import { fishStockLogApi } from '../utils/fishStockLogApi'
import { overlayFishStockOnProductionRows } from './biomass/fishStockLogBridge'
import { computeDisplayedEndOfDayStockByRowId } from '../utils/productionEndOfDayStock'
import { scopeLogsToLotBucket } from '../utils/biomassLotScoping'
import { loadSfrMatrix } from '../utils/sfrMatrixApi'
import { getDefaultMatrixForSpecies, loadCachedMatrix, resolveSfrMatrixPresetKey } from '../utils/sfrMatrix'
import { resolveEffectiveSfrMatrix } from '../utils/sfrProfiles'
import { useT } from '../i18n'
import { getProductionSpeciesLabel } from '../utils/productionSpeciesLabels'
import { translateActivityText } from '../utils/activityDisplay'
import { resolveCanonicalLotKey } from '../utils/lotIdentity'
import { resolveDashboardRowLotKey, resolveDashboardSelectionLotKey } from '../utils/dashboardLotIdentity'
import { loadReports, type ReportDefinitionV1 } from '../utils/reportStorage'
import { loadDashboard, type PivotWidgetV1, type WidgetLayout } from '../utils/dashboardStorage'
import { loadHomeWidgetPins, setHomeWidgetLayouts, setHomeWidgetPinned, type HomeWidgetPinV1 } from '../utils/homeWidgetsStorage'
import { seedDefaultPanelAndHomeContent } from '../utils/panelDefaults'
import { usePanelWidgetReportRows } from '../hooks/usePanelWidgetReportRows'
import { PanelWidgetCard } from '../components/PanelWidgetCard'
import styles from '../components/PanelWidgetCard.module.css'
import Drawer from '../components/Drawer'
import type { Layout } from 'react-grid-layout'
import {
  ResponsiveGridLayout,
  GRID_BREAKPOINT_DESKTOP,
  GRID_BREAKPOINTS,
  GRID_COLS,
  GRID_ROW_HEIGHT,
  buildLayoutItem,
  computeChangedLayoutEntries
} from '../utils/panelWidgetGrid'
import type { Farm, ProductionUnit } from '../types/farm'
import type { Lot } from '../types/lot'
import {
  loadStoredSpecies,
  persistSpeciesSelection,
  PRODUCTION_SPECIES_PRESETS,
  type ProductionSpeciesKey
} from '../data/productionSpeciesPresets'
import { resolveFarmProductionSpeciesKey } from '../utils/farmProductionSpecies'
import type { Task, TaskDashboardSummary } from '../types/task'
import type { ActivityEvent } from '../types/activity'
import { farmToday } from '../utils/farmTime'
import { dropEmptySameDayDuplicateRows } from '../utils/emptyDayRows'
import { useDeletedLotRowMatcher } from '../hooks/useDeletedLotRowMatcher'
import { withoutDeletedLotLogs } from '../utils/deletedLots'

type ProductionUnitLookupEntry = {
  farmId: string
  farmName: string
  productionUnitName: string
}

type DataRowSource = 'api' | 'local'

type DatabaseRowWithKey = BaseDatabaseRow & {
  lotId?: string
  _farmKey: string
  _canonicalFarmKey: string
  _canonicalProductionUnitKey: string
  _canonicalLotKey: string
  _source: DataRowSource
  _displayFarmLabel?: string
}

type HistoricalRowMatch = DatabaseRowWithKey & {
  _matchedFor: string
}

type KpiState = {
  avgWaterTemp: number
  avgWaterTempTrend: number
  avgWaterPh: number
  avgWaterPhTrend: number
  avgWaterDo: number
  avgWaterDoTrend: number
  avgWaterSalinity: number
  avgWaterSalinityTrend: number
  dailyFeed: number
  dailyFeedTrend: number
  actualDailyFeed: number
  actualDailyFeedTrend: number
  actualDailyFeedEvents: number
  growthRate: number
  growthTrend: number
  mortalityRate: number
  mortalityTrend: number
}

type ProductionUnitResolution = {
  farmId: string
  farmName: string
  productionUnitId: string
  productionUnitName: string
  canonicalGroupKey: string
  storageKey: string
  canonicalDisplayLabel: string
  aliasKeys: Set<string>
}

type ResolvedAliasRecord = {
  aliasKey: string
  displayLabel?: string
  canonicalFarmId: string
  canonicalProductionUnitId: string
}

type NormalizationIssue =
  | { type: 'ambiguous-alias'; alias: string; candidates: string[] }
  | { type: 'unresolved-alias'; alias: string }

type LegacyRewritePlan = {
  shouldRewrite: true
  updatedData: Record<string, unknown>
  originalData: Record<string, unknown>
  backupKey: string
  summary: {
    migratedKeys: number
    removedLegacyKeys: string[]
    unresolvedKeys: string[]
  }
}

type NormalizationResult = {
  rowsByDisplayKey: Record<string, DatabaseRowWithKey[]>
  warnings: NormalizationIssue[]
  rewritePlan: LegacyRewritePlan | null
  canonicalDisplayLabels: Map<string, string>
  resolvedAliases: ResolvedAliasRecord[]
  unresolvedAliasKeys: string[]
}

type ActualFeedDebugSummary = {
  currentWeekKey?: string
  previousWeekKey?: string
  currentWeeklyKg: number
  previousWeeklyKg: number
  currentEvents: number
  previousEvents: number
}

type WaterQualityKpi = {
  current: number
  previous: number | null
  trend: number
  matchedLots: number
  totalLots: number
}

const ALL_PRODUCTION_UNITS_ID = '__all__'

const emptyWaterQualityKpi = (): WaterQualityKpi => ({
  current: 0,
  previous: null,
  trend: 0,
  matchedLots: 0,
  totalLots: 0
})

type MortalityDebugRow = {
  farmKey: string
  lot: string
  currentDate?: string
  previousDate?: string
  baselineDate?: string
  currentStock?: number
  previousStock?: number
  baselineStock?: number
  mortalityCount?: number
  mortalityRate?: number
  previousMortalityCount?: number
  previousMortalityRate?: number
  baselineGapDays?: number
  gapDays?: number
  gapFlag?: boolean
  notes: string[]
  included: boolean
  _matchedFor?: string
}

type MortalityDebugSummary = {
  matchedLots: number
  totalLots: number
  currentMortalityCount: number
  currentPreviousStock: number
  currentRate: number
  previousMortalityCount: number
  previousPreviousStock: number
  previousRate: number | null
  unmatchedLots: string[]
  gapWarningLots: string[]
  longGapLots: string[]
}

type GrowthDebugRow = {
  farmKey: string
  lot: string
  currentDate?: string
  previousDate?: string
  baselineDate?: string
  gapDays?: number
  baselineGapDays?: number
  currentWeight?: number
  previousWeight?: number
  baselineWeight?: number
  currentStock?: number
  previousStock?: number
  baselineStock?: number
  dailyGain?: number
  previousDailyGain?: number
  sgr?: number
  previousSgr?: number
  notes: string[]
  included: boolean
}

type GrowthDebugSummary = {
  matchedLots: number
  totalLots: number
  averageDailyGain: number
  averageSGR: number | null
  historicalDailyGain: number | null
  historicalSGR: number | null
  unmatchedLots: string[]
  gapWarningLots: string[]
  longGapLots: string[]
}

type CanonicalLotGroup = {
  canonicalProductionUnitKey: string
  canonicalLotKey: string
  displayLabel: string
  rows: DatabaseRowWithKey[]
}

type CanonicalLotGroupAccumulator = CanonicalLotGroup & {
  labels: Set<string>
  rowIds: Set<string>
}

const scoreRowForCanonicalGrouping = (row: DatabaseRowWithKey): number => {
  let score = 0
  if (row._displayFarmLabel) score += 4
  if (row._displayFarmLabel && row._displayFarmLabel.includes('·')) score += 2
  if (row._canonicalProductionUnitKey) score += 2
  if (row._canonicalFarmKey) score += 1
  if (row._canonicalLotKey) score += 1
  return score
}

const buildCanonicalLotGroups = (rows: DatabaseRowWithKey[], canonicalLabels?: Map<string, string>): CanonicalLotGroup[] => {
  const groups = new Map<string, CanonicalLotGroupAccumulator>()
  const rowKeyById = new Map<string, { productionUnitKey: string; lotKey: string }>()

  const orderedRows = rows.slice().sort((a, b) => {
    const scoreDiff = scoreRowForCanonicalGrouping(b) - scoreRowForCanonicalGrouping(a)
    if (scoreDiff !== 0) return scoreDiff

    const aDate = a.date ? new Date(a.date).getTime() : 0
    const bDate = b.date ? new Date(b.date).getTime() : 0
    return bDate - aDate
  })

  orderedRows.forEach((row) => {
    const productionUnitCandidate = row._canonicalProductionUnitKey
      || row._canonicalFarmKey
      || canonicalizeKey(row._displayFarmLabel)
      || canonicalizeKey(row._farmKey)
      || row._farmKey
      || 'unknown-production-unit'
    const lotCandidate = resolveDashboardRowLotKey(row)

    let productionUnitKey = productionUnitCandidate
    let lotKey = lotCandidate

    if (row.id) {
      const existing = rowKeyById.get(row.id)
      if (existing) {
        productionUnitKey = existing.productionUnitKey
        lotKey = existing.lotKey
      } else {
        rowKeyById.set(row.id, { productionUnitKey, lotKey })
      }
    }

    const compositeKey = `${productionUnitKey}::${lotKey}`

    if (!groups.has(compositeKey)) {
      groups.set(compositeKey, {
        canonicalProductionUnitKey: productionUnitKey,
        canonicalLotKey: lotKey,
        displayLabel: 'Unknown Facility',
        rows: [],
        labels: new Set<string>(),
        rowIds: new Set<string>()
      })
    }

    const group = groups.get(compositeKey)!

    const candidateLabels: Array<string | undefined> = [
      canonicalLabels?.get(productionUnitKey),
      canonicalLabels?.get(row._canonicalProductionUnitKey ?? ''),
      canonicalLabels?.get(row._canonicalFarmKey ?? ''),
      row._displayFarmLabel,
      row._farmKey
    ]

    candidateLabels
      .filter((label): label is string => Boolean(label && label.trim() !== ''))
      .forEach((label) => group.labels.add(label))

    const resolvedLabel = selectDisplayLabel(group.labels)
    if (resolvedLabel && resolvedLabel !== group.displayLabel) {
      group.displayLabel = resolvedLabel
    }

    if (row.id) {
      if (group.rowIds.has(row.id)) {
        return
      }
      group.rowIds.add(row.id)
    }

    group.rows.push(row)
  })

  return Array.from(groups.values()).map((group) => ({
    canonicalProductionUnitKey: group.canonicalProductionUnitKey,
    canonicalLotKey: group.canonicalLotKey,
    displayLabel: group.displayLabel || selectDisplayLabel(group.labels),
    rows: group.rows
  }))
}

const MS_PER_DAY = 86_400_000

const toKey = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return ''
  return String(value)
}

const resolveFarmKey = (farm: Farm): string => toKey(farm.id) || farm.name || ''
const resolveProductionUnitKey = (unit: ProductionUnit): string => toKey(unit.id) || unit.name || ''

const canonicalizeLabel = (value: string | null | undefined): string => {
  if (!value) return ''
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const canonicalizeKey = (...parts: Array<string | null | undefined>): string | undefined => {
  const tokens = parts
    .map((part) => canonicalizeLabel(part))
    .filter((token) => token.length > 0)
  if (tokens.length === 0) return undefined
  return tokens.join('::')
}

const levenshteinDistance = (a: string, b: string): number => {
  if (a === b) return 0
  if (a.length === 0) return b.length
  if (b.length === 0) return a.length

  const previous = new Array(b.length + 1)
  const current = new Array(b.length + 1)

  for (let j = 0; j <= b.length; j += 1) {
    previous[j] = j
  }

  for (let i = 1; i <= a.length; i += 1) {
    current[0] = i
    const aChar = a.charCodeAt(i - 1)

    for (let j = 1; j <= b.length; j += 1) {
      const bChar = b.charCodeAt(j - 1)
      const cost = aChar === bChar ? 0 : 1
      current[j] = Math.min(
        current[j - 1] + 1,
        previous[j] + 1,
        previous[j - 1] + cost
      )
    }

    for (let j = 0; j <= b.length; j += 1) {
      previous[j] = current[j]
    }
  }

  return current[b.length]
}

const normalizeStage = (value: unknown): DatabaseRowWithKey['stage'] => {
  if (typeof value === 'string') {
    const cleaned = value.trim().toLowerCase()
    if (cleaned === 'active' || cleaned === 'activo' || cleaned === 'activa' || cleaned === '1') {
      return 'Active'
    }
    if (cleaned === 'inactive' || cleaned === 'inactivo' || cleaned === 'inactiva' || cleaned === '0') {
      return 'Inactive'
    }
  }

  if (typeof value === 'number') {
    return value > 0 ? 'Active' : 'Inactive'
  }

  if (typeof value === 'boolean') {
    return value ? 'Active' : 'Inactive'
  }

  // Default to Active so historical rows without explicit stage aren't discarded
  return 'Active'
}

const selectDisplayLabel = (labels: Set<string>): string => {
  if (labels.size === 0) return 'Unknown Facility'
  const ordered = Array.from(labels)
    .map((label) => label?.trim())
    .filter((label): label is string => Boolean(label && label.length > 0))
  if (ordered.length === 0) return 'Unknown Facility'

  const scoreLabel = (label: string) => {
    const hasAlpha = /[a-z]/i.test(label) ? 1 : 0
    const hasSeparator = /[·\s]/.test(label) ? 1 : 0
    const hasNoLetters = /^[^a-zA-Z]+$/.test(label) ? 1 : 0
    return {
      hasAlpha,
      hasSeparator,
      hasNoLetters,
      length: label.length,
      value: label
    }
  }

  ordered.sort((rawA, rawB) => {
    const a = scoreLabel(rawA)
    const b = scoreLabel(rawB)

    if (a.hasAlpha !== b.hasAlpha) return b.hasAlpha - a.hasAlpha
    if (a.hasSeparator !== b.hasSeparator) return b.hasSeparator - a.hasSeparator
    if (a.hasNoLetters !== b.hasNoLetters) return a.hasNoLetters - b.hasNoLetters
    if (a.length !== b.length) return b.length - a.length
    return a.value.localeCompare(b.value)
  })

  return ordered[0]
}

const Dashboard: React.FC = () => {
  const initialSelection = useMemo(() => loadDashboardSelection(), [])
  const navigate = useNavigate()
  const t = useT()

  const [farms, setFarms] = useState<Farm[]>([])
  // Findings #200: rows of a deleted lot stay saved but are left out of Panel.
  const deletedLotFarmIds = useMemo(() => farms.map((farm) => String(farm?.id ?? '')), [farms])
  const isDeletedLotRow = useDeletedLotRowMatcher(deletedLotFarmIds)
  const [lots, setLots] = useState<Lot[]>([])
  const [loadingData, setLoadingData] = useState<boolean>(true)
  const [dataError, setDataError] = useState<string | null>(null)
  const [totalFishStock, setTotalFishStock] = useState<number>(0)
  const [activeFishRows, setActiveFishRows] = useState<DatabaseRowWithKey[]>([])
  const [fishStockTrend, setFishStockTrend] = useState<number>(0)
  const [fishStockDebug, setFishStockDebug] = useState<FishStockDebugInfo | null>(null)
  const [feedDebugRows, setFeedDebugRows] = useState<FeedDebugRow[]>([])
  const [feedDebugSummary, setFeedDebugSummary] = useState<FeedDebugSummary | null>(null)
  const [actualFeedDebugSummary, setActualFeedDebugSummary] = useState<ActualFeedDebugSummary | null>(null)
  const [growthDebugRows, setGrowthDebugRows] = useState<GrowthDebugRow[]>([])
  const [growthDebugSummary, setGrowthDebugSummary] = useState<GrowthDebugSummary | null>(null)
  const [kpiData, setKpiData] = useState<KpiState>({
    avgWaterTemp: 0,
    avgWaterTempTrend: 0,
    avgWaterPh: 0,
    avgWaterPhTrend: 0,
    avgWaterDo: 0,
    avgWaterDoTrend: 0,
    avgWaterSalinity: 0,
    avgWaterSalinityTrend: 0,
    dailyFeed: 0,
    dailyFeedTrend: 0,
    actualDailyFeed: 0,
    actualDailyFeedTrend: 0,
    actualDailyFeedEvents: 0,
    growthRate: 0,
    growthTrend: 0,
    mortalityRate: 0,
    mortalityTrend: 0
  })
  const [waterQualityKpis, setWaterQualityKpis] = useState(() => ({
    temp: emptyWaterQualityKpi(),
    ph: emptyWaterQualityKpi(),
    dissolvedOxygen: emptyWaterQualityKpi(),
    salinity: emptyWaterQualityKpi()
  }))
  const [mortalityDebugRows, setMortalityDebugRows] = useState<MortalityDebugRow[]>([])
  const [mortalityDebugSummary, setMortalityDebugSummary] = useState<MortalityDebugSummary | null>(null)
  const [currentFarm, setCurrentFarm] = useState<Farm | null>(null)
  const [refreshTime, setRefreshTime] = useState<Date>(new Date())
  const [dashboardDataRevision, setDashboardDataRevision] = useState<number>(0)
  const [homePins, setHomePins] = useState<HomeWidgetPinV1[]>([])
  const [allTenantWidgets, setAllTenantWidgets] = useState<PivotWidgetV1[]>([])
  const [homeGridBreakpoint, setHomeGridBreakpoint] = useState<string>(GRID_BREAKPOINT_DESKTOP)
  const [isAddWidgetPickerOpen, setIsAddWidgetPickerOpen] = useState<boolean>(false)
  const [productionTableSnapshot, setProductionTableSnapshot] = useState<Record<string, unknown>>({})
  const loggedDiagnosticBucketsRef = useRef<Set<string>>(new Set())
  const [selectedSpeciesKey, setSelectedSpeciesKey] = useState<ProductionSpeciesKey>(() => loadStoredSpecies())
  const selectedSfrMatrixKey = useMemo(() => resolveSfrMatrixPresetKey(selectedSpeciesKey), [selectedSpeciesKey])
  const [sfrMatrix, setSfrMatrix] = useState<MatrixData>(() => getDefaultMatrixForSpecies(loadStoredSpecies()))
  const [prefetchedSfrMatricesBySpecies, setPrefetchedSfrMatricesBySpecies] = useState<Partial<Record<ProductionSpeciesKey, MatrixData>>>({})
  const [selectedFarmId, setSelectedFarmId] = useState<string>(initialSelection.farmId)
  const [selectedProductionUnitId, setSelectedProductionUnitId] = useState<string>(initialSelection.productionUnitId)
  const [selectedLotId, setSelectedLotId] = useState<string>(initialSelection.lotId)
  const [taskDashboardSummary, setTaskDashboardSummary] = useState<TaskDashboardSummary | null>(null)
  const [taskDashboardSummaryError, setTaskDashboardSummaryError] = useState<string | null>(null)
  const [systemAlertTasks, setSystemAlertTasks] = useState<Task[]>([])
  const [recentActivity, setRecentActivity] = useState<ActivityEvent[]>([])
  const [recentActivityError, setRecentActivityError] = useState<string | null>(null)

  const speciesPresetLabel = selectedFarmId
    ? getProductionSpeciesLabel(t, selectedSpeciesKey)
    : '—'

  const stockMetricLabel = selectedSpeciesKey === 'shrimp'
    ? t('home.stockMetric.shrimp')
    : t('home.stockMetric.fish')

  useEffect(() => {
    if (loadingData) return

    let cancelled = false
    void (async () => {
      try {
        await ensureOfflineReady()
        const entities = await dexieProductionTableRepo.listAllEntities()

        const farmById = new Map<string, Farm>()
        const unitByFarmAndId = new Map<string, ProductionUnit>()
        farms.forEach((farm) => {
          const farmId = String(farm?.id ?? '')
          if (farmId) farmById.set(farmId, farm)
          ;(farm.productionUnits ?? []).forEach((unit) => {
            const unitId = String(unit?.id ?? '')
            if (!farmId || !unitId) return
            unitByFarmAndId.set(`${farmId}::${unitId}`, unit)
          })
        })

        const buckets = new Map<string, BiomassDataRow[]>()
        const bucketContext = new Map<string, { farmId: string; productionUnitId: string; lotIds: Set<string> }>()
        const attributableLotIds = new Set<string>()

        entities.forEach((entity) => {
          const farm = farmById.get(String(entity.farmId))
          const unit = unitByFarmAndId.get(`${String(entity.farmId)}::${String(entity.productionUnitId)}`)

          const farmName = String(farm?.name ?? `Farm ${entity.farmId}`)
          const unitName = String(unit?.name ?? `Unit ${entity.productionUnitId}`)
          const storageKey = `${farmName}-${unitName}`

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { pk, updatedAt, ...row } = entity as any
          const normalizedRow: any = { ...row }
          if (isDeletedLotRow(normalizedRow, String(entity.productionUnitId ?? ''))) return

          const bucket = buckets.get(storageKey)
          if (bucket) bucket.push(normalizedRow)
          else buckets.set(storageKey, [normalizedRow])

          let ctx = bucketContext.get(storageKey)
          if (!ctx) {
            ctx = {
              farmId: String(entity.farmId ?? ''),
              productionUnitId: String(entity.productionUnitId ?? ''),
              lotIds: new Set<string>()
            }
            bucketContext.set(storageKey, ctx)
          }

          const rowLotId = String(normalizedRow.lotId ?? '').trim()
          if (rowLotId) {
            ctx.lotIds.add(rowLotId)
            attributableLotIds.add(rowLotId)
          }
        })

        // T3-C-02b (2026-07-13): load MortalityLog events (post-MORT-12,
        // the canonical source for daily mortality) AND FishStockLog
        // events (post-T3-A-02, the canonical source for fish-stock
        // counts). Both overlays chain onto the production rows before
        // we compute the end-of-day stock derivation, so KPI consumers
        // reading `row.fishStock` see (a) canonical event-derived stock
        // when a FishStockLog exists for the (lot, day), and (b) a
        // mortality-derived carryover for days that only recorded
        // mortality. Prior to T3-C-02b Dashboard loaded only
        // MortalityLog, so lots whose stock lived exclusively in
        // FishStockLog events reported 0 across every home KPI.
        const [storedMortalityLogs, storedFishStockLogs] = await Promise.all([
          mortalityLogApi
            .list({}, { preferLocal: true })
            .catch(() => [] as Awaited<ReturnType<typeof mortalityLogApi.list>>),
          fishStockLogApi
            .list({}, { preferLocal: true })
            .catch(() => [] as Awaited<ReturnType<typeof fishStockLogApi.list>>)
        ])
        // Findings #207: a deleted lot's logs never count for an active lot with the same name.
        const allMortalityLogs = withoutDeletedLotLogs(storedMortalityLogs, isDeletedLotRow)
        const allFishStockLogs = withoutDeletedLotLogs(storedFishStockLogs, isDeletedLotRow)

        const snapshot: Record<string, unknown> = {}
        buckets.forEach((rows, key) => {
          // A1.1 Risk #2 fix: route logs by lotId when a production row
          // claims it, location only as fallback — otherwise a
          // transferred lot's logs never reach the bucket holding its
          // pre-transfer rows and every home KPI reading those cells
          // falls back to raw stored values. Shared with reportDatasets
          // and currentBiomassPerLot via `scopeLogsToLotBucket`.
          const ctx = bucketContext.get(key)
          const scopedMortalityLogs = ctx
            ? scopeLogsToLotBucket(allMortalityLogs, ctx, attributableLotIds)
            : []
          const scopedFishStockLogs = ctx
            ? scopeLogsToLotBucket(allFishStockLogs, ctx, attributableLotIds)
            : []

          // Chain order matches Biomass.tsx samplingRows: mortality FIRST
          // (populates `dailyMortality` from event stream), then
          // FishStockLog OUTSIDE (populates `fishStock` from LATEST-
          // VALUE-WINS rollup). The end-of-day stock derivation then
          // consumes the fully-overlaid rows.
          const overlaid = overlayFishStockOnProductionRows(
            overlayMortalityLogsOnProductionRows(dropEmptySameDayDuplicateRows(rows), scopedMortalityLogs),
            scopedFishStockLogs
          )
          const derivedByRowId = computeDisplayedEndOfDayStockByRowId(overlaid)

          const enriched = overlaid.map((row) => {
            let next: BiomassDataRow = row

            // If the row's stored `fishStock` is blank/non-positive, fall back
            // to the derived end-of-day stock so downstream KPIs that read
            // `row.fishStock` (computeFishStockKpi etc.) see a non-zero value
            // for event-only mortality lots.
            const storedRaw = (next as any).fishStock
            let storedStr = ''
            if (typeof storedRaw === 'string') {
              storedStr = storedRaw.trim()
            } else if (storedRaw !== null && storedRaw !== undefined) {
              storedStr = String(storedRaw).trim()
            }
            const storedNum = storedStr ? Number(storedStr) : Number.NaN
            if (Number.isFinite(storedNum) && storedNum > 0) return next

            const derived = derivedByRowId.get(String(next.id ?? ''))
            if (derived && typeof derived.numericValue === 'number' && derived.numericValue > 0) {
              next = { ...next, fishStock: String(derived.numericValue) }
            }
            return next
          })

          snapshot[key] = enriched
        })

        if (!cancelled) setProductionTableSnapshot(snapshot)
      } catch {
        if (!cancelled) setProductionTableSnapshot({})
      }
    })()

    return () => {
      cancelled = true
    }
  }, [dashboardDataRevision, farms, loadingData, isDeletedLotRow])

  // v2.2 #40/#41/#42: the old inline fish stock/feed/growth/mortality KPI
  // cards were removed for being hard to maintain (a sixth duplicate
  // calculation per metric) with no replacement shipped on /home. Rather
  // than reviving them, /home surfaces whichever Panel widgets *this user*
  // (not the whole tenant) has chosen via homeWidgetsStorage.ts — same
  // `PanelWidgetCard` rendering and `reportDatasets.ts` pipeline Panel
  // itself uses, zero new calculation or chart logic here. The widget
  // *definitions* stay tenant-wide (`loadDashboard`); only the selection
  // and arrangement on /home are personal.
  // v2.2 findings.md #48: seed the default Panel reports/widgets and this
  // user's one-time default Home pins before the first read, so a brand-new
  // user who lands here before ever opening Panel still gets a populated
  // Home. Idempotent; the ref just avoids redoing it on every revision.
  const hasSeededDefaultsRef = useRef(false)
  useEffect(() => {
    if (!hasSeededDefaultsRef.current) {
      hasSeededDefaultsRef.current = true
      seedDefaultPanelAndHomeContent(t)
    }
    setHomePins(loadHomeWidgetPins())
    setAllTenantWidgets(loadDashboard().widgets)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardDataRevision])

  const tenantWidgetsById = useMemo(
    () => new Map(allTenantWidgets.map((widget) => [widget.id, widget] as const)),
    [allTenantWidgets]
  )

  // Filters out a pin whose widget was since deleted in Panel — never
  // shown as a broken card, just quietly absent until the user picks
  // something else to feature.
  const pinnedWidgetEntries = useMemo(() => (
    homePins
      .map((pin) => {
        const widget = tenantWidgetsById.get(pin.widgetId)
        return widget ? { pin, widget } : null
      })
      .filter((entry): entry is { pin: HomeWidgetPinV1; widget: PivotWidgetV1 } => Boolean(entry))
  ), [homePins, tenantWidgetsById])

  const availableWidgetsToAdd = useMemo(() => {
    const pinnedIds = new Set(homePins.map((pin) => pin.widgetId))
    return allTenantWidgets.filter((widget) => !pinnedIds.has(widget.id))
  }, [allTenantWidgets, homePins])

  const reportsById = useMemo(() => {
    const map = new Map<string, ReportDefinitionV1>()
    loadReports().forEach((report) => map.set(report.id, report))
    return map
  }, [dashboardDataRevision])

  const pinnedWidgetReports = useMemo(() => {
    const uniqueReportIds = new Set(pinnedWidgetEntries.map((entry) => entry.widget.reportId))
    return Array.from(uniqueReportIds)
      .map((reportId) => reportsById.get(reportId))
      .filter((report): report is ReportDefinitionV1 => Boolean(report))
  }, [pinnedWidgetEntries, reportsById])

  const { runStates: pinnedWidgetRunStates } = usePanelWidgetReportRows(pinnedWidgetReports)

  const homeGridLayout: Layout = useMemo(
    () => pinnedWidgetEntries.map((entry, index) => buildLayoutItem(entry.widget.id, index, entry.pin.layout, entry.widget.width)),
    [pinnedWidgetEntries]
  )

  const handleHomeGridInteractionStop = (...args: unknown[]) => {
    const newLayout = args[0] as Layout
    if (homeGridBreakpoint !== GRID_BREAKPOINT_DESKTOP) return

    const changedEntries = computeChangedLayoutEntries(
      newLayout,
      (id) => pinnedWidgetEntries.find((entry) => entry.widget.id === id)?.pin.layout
    )
    if (changedEntries.length === 0) return

    setHomePins(setHomeWidgetLayouts(changedEntries))
  }

  const handleAddWidgetToHome = (widgetId: string) => {
    setHomePins(setHomeWidgetPinned(widgetId, true))
  }

  const handleRemoveWidgetFromHome = (widgetId: string) => {
    setHomePins(setHomeWidgetPinned(widgetId, false))
  }

  const stockUnitLabel = selectedSpeciesKey === 'shrimp'
    ? t('home.stockUnit.shrimp')
    : t('home.stockUnit.fish')
  const stockEmoji = selectedSpeciesKey === 'shrimp' ? '🦐' : '🐟'
  const stockCountLabel = selectedSpeciesKey === 'shrimp'
    ? t('home.stockCountLabel.shrimp')
    : t('home.stockCountLabel.fish')

  useEffect(() => {
    if (!selectedFarmId) return
    const selectedFarm = farms.find((farm) => resolveFarmKey(farm) === selectedFarmId)
    const farmSpeciesKey = resolveFarmProductionSpeciesKey(selectedFarm)
    if (farmSpeciesKey && farmSpeciesKey !== selectedSpeciesKey) {
      setSelectedSpeciesKey(farmSpeciesKey)
      persistSpeciesSelection(farmSpeciesKey)
    }
  }, [farms, selectedFarmId, selectedSpeciesKey])

  useEffect(() => {
    let cancelled = false

    const speciesToLoad = new Set<ProductionSpeciesKey>([selectedSpeciesKey])
    farms.forEach((farm) => {
      const farmSpecies = resolveFarmProductionSpeciesKey(farm)
      if (farmSpecies) {
        speciesToLoad.add(farmSpecies)
      }
    })

    const fallbackMatrices: Partial<Record<ProductionSpeciesKey, MatrixData>> = {}
    speciesToLoad.forEach((species) => {
      const presetKey = resolveSfrMatrixPresetKey(species)
      fallbackMatrices[species] = loadCachedMatrix(presetKey) ?? getDefaultMatrixForSpecies(presetKey)
    })

    setPrefetchedSfrMatricesBySpecies((prev) => ({
      ...prev,
      ...fallbackMatrices
    }))
    setSfrMatrix(fallbackMatrices[selectedSpeciesKey] ?? getDefaultMatrixForSpecies(selectedSfrMatrixKey))

    const load = async () => {
      const entries = await Promise.all(
        Array.from(speciesToLoad).map(async (species) => {
          const result = await loadSfrMatrix(resolveSfrMatrixPresetKey(species), { preferRecentLocal: true })
          return { species, result }
        })
      )

      if (cancelled) return

      const nextMatrices: Partial<Record<ProductionSpeciesKey, MatrixData>> = {}

      entries.forEach(({ species, result }) => {
        nextMatrices[species] = result.matrix

        if (result.errorMessage) {
          console.warn(`Dashboard: failed to refresh ${species} SFR matrix; using local/default matrix`, result.errorMessage)
        }
      })

      setPrefetchedSfrMatricesBySpecies((prev) => ({
        ...prev,
        ...nextMatrices
      }))
      setSfrMatrix(nextMatrices[selectedSpeciesKey] ?? fallbackMatrices[selectedSpeciesKey] ?? getDefaultMatrixForSpecies(selectedSfrMatrixKey))
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [farms, selectedSfrMatrixKey, selectedSpeciesKey])

  const loadData = useCallback(async (options?: { forceNetwork?: boolean }) => {
    setLoadingData(true)
    setDataError(null)

    const canAttemptNetwork = (): boolean => {
      if (typeof navigator === 'undefined') return true
      return navigator.onLine
    }

    const mergeById = <T extends { id: string }>(localItems: T[], remoteItems: T[]): T[] => {
      const merged = new Map<string, T>()
      localItems.forEach((item) => {
        if (item?.id) merged.set(item.id, item)
      })
      remoteItems.forEach((item) => {
        if (item?.id) merged.set(item.id, item)
      })
      return Array.from(merged.values())
    }

    const normalizeFarmsForDashboard = (candidate: unknown): Farm[] => {
      if (!Array.isArray(candidate)) return []
      return candidate
        .filter((farm) => farm && typeof farm === 'object')
        .map((farm: any) => {
          const productionUnits = Array.isArray(farm.productionUnits) ? farm.productionUnits : []
          const normalizedUnits = productionUnits
            .filter((unit: any) => unit && typeof unit === 'object')
            .map((unit: any) => ({
              ...unit,
              farmId: typeof unit.farmId === 'string' && unit.farmId.trim().length > 0 ? unit.farmId : String(farm.id ?? '')
            }))

          return {
            ...farm,
            productionUnits: normalizedUnits
          } as Farm
        })
    }

    let localFarms: Farm[] = []
    let localLots: Lot[] = []

    try {
      await ensureOfflineReady()
      const [farmsFromDb, lotsFromDb] = await Promise.all([
        dexieFarmRepo.list(),
        dexieLotRepo.list()
      ])

      localFarms = normalizeFarmsForDashboard(farmsFromDb as any)
      localLots = Array.isArray(lotsFromDb) ? (lotsFromDb as Lot[]) : []

      setFarms(localFarms)
      setLots(localLots)
    } catch (error) {
      console.warn('[dashboard] failed to load local data', error)
    }

    if (!canAttemptNetwork()) {
      setLoadingData(false)
      return
    }

    try {
      const noCache = Boolean(options?.forceNetwork)
      const [farmsList, unitsList, lotsList] = await Promise.all([
        api.farms.list(noCache ? { noCache: true } : undefined),
        api.productionUnits.list(noCache ? { noCache: true } : undefined),
        api.lots.list(noCache ? { noCache: true } : undefined)
      ])

      const unitsByFarm = unitsList.reduce<Record<string, ProductionUnit[]>>((acc, unit) => {
        if (!unit?.farmId) return acc
        if (!acc[unit.farmId]) {
          acc[unit.farmId] = []
        }
        acc[unit.farmId].push(unit)
        return acc
      }, {})

      const farmsWithUnits = farmsList.map((farm) => ({
        ...farm,
        productionUnits: unitsByFarm[farm.id] ?? farm.productionUnits ?? []
      }))

      const mergedFarms = mergeById(localFarms, farmsWithUnits)
      const mergedLots = mergeById(localLots, lotsList)

      setFarms(mergedFarms)
      setLots(mergedLots)

      try {
        await ensureOfflineReady()
        await Promise.all([
          dexieFarmRepo.upsertMany(farmsWithUnits as any),
          dexieLotRepo.upsertMany(lotsList as any)
        ])
      } catch (error) {
        console.warn('[dashboard] failed to persist refreshed data locally', error)
      }
    } catch (error) {
      console.error('[dashboard] online refresh failure', error)
      const message = error instanceof Error ? error.message : 'Unable to refresh dashboard data'
      // Manual refresh should surface failures even if we have local data.
      if (options?.forceNetwork || (localFarms.length === 0 && localLots.length === 0)) {
        setDataError(message)
      }
    } finally {
      setLoadingData(false)
    }
  }, [])

  const productionUnitLookup = useMemo<Record<string, ProductionUnitLookupEntry>>(() => {
    const lookup: Record<string, ProductionUnitLookupEntry> = {}

    farms.forEach((farm) => {
      const farmKey = resolveFarmKey(farm)
      farm.productionUnits?.forEach((unit) => {
        const unitKey = resolveProductionUnitKey(unit)
        if (!unitKey) return
        lookup[unitKey] = {
          farmId: farmKey,
          farmName: farm.name,
          productionUnitName: unit.name
        }

        const rawIdKey = toKey(unit.id)
        if (rawIdKey && rawIdKey !== unitKey) {
          lookup[rawIdKey] = {
            farmId: farmKey,
            farmName: farm.name,
            productionUnitName: unit.name
          }
        }
      })
    })

    return lookup
  }, [farms])

  const farmMap = useMemo(() => {
    const map = new Map<string, Farm>()
    farms.forEach((farm) => {
      const normalizedKey = resolveFarmKey(farm)
      const rawIdKey = toKey(farm.id)
      if (normalizedKey) {
        map.set(normalizedKey, farm)
      }
      if (rawIdKey && rawIdKey !== normalizedKey) {
        map.set(rawIdKey, farm)
      }
      if (farm.name && farm.name !== normalizedKey) {
        map.set(farm.name, farm)
      }
    })
    return map
  }, [farms])

  const selectedFarm = selectedFarmId ? farmMap.get(selectedFarmId) ?? null : null
  const selectedFarmTimeZone = useMemo(() => resolveTimeZone(getTimeZoneFromFarm(selectedFarm)), [selectedFarm])

  const derivedProductionUnits = useMemo<ProductionUnit[]>(() => {
    const farmEntityId = selectedFarm ? toKey((selectedFarm as any).id) : ''
    if (!farmEntityId) return []

    const byId = new Map<string, ProductionUnit>()
    lots.forEach((lot) => {
      if (toKey((lot as any).farmId) !== farmEntityId) return
      const unitId = toKey((lot as any).productionUnitId)
      if (!unitId) return

      if (!byId.has(unitId)) {
        byId.set(unitId, {
          id: unitId,
          name: ''
        } as ProductionUnit)
      }
    })

    const orderedIds = Array.from(byId.keys()).sort((a, b) => a.localeCompare(b))
    return orderedIds.map((unitId, index) => ({
      ...(byId.get(unitId) as ProductionUnit),
      farmId: farmEntityId,
      name: `PU ${index + 1}`
    }))
  }, [lots, selectedFarm])

  const availableProductionUnits = useMemo(() => {
    const units = selectedFarm?.productionUnits ?? []
    if (units.length > 0) return units
    return derivedProductionUnits
  }, [derivedProductionUnits, selectedFarm])
  const selectedProductionUnit = selectedProductionUnitId && selectedProductionUnitId !== ALL_PRODUCTION_UNITS_ID
    ? availableProductionUnits.find((unit) => resolveProductionUnitKey(unit) === selectedProductionUnitId) ?? null
    : null

  const normalizeUnitSelection = useCallback((candidate: string, farmId: string): string => {
    const trimmed = typeof candidate === 'string' ? candidate.trim() : ''
    if (!trimmed) return ''
    if (trimmed === ALL_PRODUCTION_UNITS_ID) return trimmed

    const farm = farmMap.get(farmId)
    const units = farm?.productionUnits ?? []

    if (units.some((unit) => resolveProductionUnitKey(unit) === trimmed)) {
      return trimmed
    }

    // Some imports (and offline states) can have farms without productionUnits.
    // Fall back to validating against lots so the selector remains usable.
    const farmEntityId = farm ? toKey((farm as any).id) : ''
    if (!farmEntityId) return ''

    const existsInLots = lots.some((lot) => (
      toKey((lot as any).farmId) === farmEntityId
      && toKey((lot as any).productionUnitId) === trimmed
    ))

    return existsInLots ? trimmed : ''
  }, [farmMap, lots])

  const normalizeLotSelection = useCallback((candidate: string, unitId: string): string => {
    if (!candidate) return ''
    if (!unitId || unitId === ALL_PRODUCTION_UNITS_ID) return ''
    const scopedLots = lots.filter((lot) => toKey(lot.productionUnitId) === unitId)
    return normalizeDashboardLotSelection(candidate, scopedLots)
  }, [lots])

  const handleFarmChange = useCallback((farmId: string) => {
    setSelectedFarmId(farmId)

    const nextUnitId = ALL_PRODUCTION_UNITS_ID
    setSelectedProductionUnitId(nextUnitId)
    setSelectedLotId('')

    persistDashboardSelection({
      farmId,
      productionUnitId: nextUnitId,
      lotId: ''
    })
  }, [])

  const handleProductionUnitChange = useCallback((productionUnitId: string) => {
    if (!selectedFarmId) {
      setSelectedProductionUnitId(ALL_PRODUCTION_UNITS_ID)
      setSelectedLotId('')
      persistDashboardSelection({ productionUnitId: ALL_PRODUCTION_UNITS_ID, lotId: '' })
      return
    }

    const nextUnitId = normalizeUnitSelection(productionUnitId, selectedFarmId) || ALL_PRODUCTION_UNITS_ID
    setSelectedProductionUnitId(nextUnitId)
    setSelectedLotId('')
    persistDashboardSelection({ productionUnitId: nextUnitId, lotId: '' })
  }, [normalizeUnitSelection, selectedFarmId])

  const handleLotChange = useCallback((lotId: string) => {
    const nextLotId = normalizeLotSelection(lotId, selectedProductionUnitId)
    setSelectedLotId(nextLotId)
    persistDashboardSelection({ lotId: nextLotId })
  }, [normalizeLotSelection, selectedProductionUnitId])

  useEffect(() => {
    const resolution = resolvePersistedDashboardLotSelection({
      loadingData,
      selectedLotId,
      selectedProductionUnitId,
      allProductionUnitsId: ALL_PRODUCTION_UNITS_ID,
      normalizeLotSelection
    })

    if (resolution.status === 'unchanged') return

    if (resolution.status === 'clear') {
      setSelectedLotId('')
      persistDashboardSelection({ lotId: '' })
      return
    }

    setSelectedLotId(resolution.lotId)
    persistDashboardSelection({ lotId: resolution.lotId })
  }, [loadingData, normalizeLotSelection, selectedLotId, selectedProductionUnitId])

  const availableLots = useMemo((): Lot[] => {
    if (!selectedProductionUnitId || selectedProductionUnitId === ALL_PRODUCTION_UNITS_ID) return []
    return lots.filter((lot) => {
      if (selectedFarmId) {
        const lotFarmKey = toKey(lot.farmId)
        if (lotFarmKey && lotFarmKey !== selectedFarmId) {
          return false
        }
      }
      const lotProductionUnitKey = toKey(lot.productionUnitId)
      return lotProductionUnitKey === selectedProductionUnitId
    })
  }, [lots, selectedFarmId, selectedProductionUnitId])

  const selectedLot = useMemo(() => {
    if (!selectedLotId) return null
    const availableMatch = findLotByDashboardSelection(selectedLotId, availableLots)
    if (availableMatch) return availableMatch
    return findLotByDashboardSelection(selectedLotId, lots)
  }, [availableLots, lots, selectedLotId])

  const selectedLotTokens = useMemo(
    () => buildDashboardLotSelectionTokens(selectedLotId, availableLots.length > 0 ? availableLots : lots),
    [availableLots, lots, selectedLotId]
  )

  const speciesDefaultMatrixBySpecies = useMemo(() => {
    const nextMap = new Map<ProductionSpeciesKey, MatrixData>()

    const ensureSpeciesMatrix = (species: ProductionSpeciesKey) => {
      if (nextMap.has(species)) {
        return
      }

      const presetKey = resolveSfrMatrixPresetKey(species)
      const matrix = prefetchedSfrMatricesBySpecies[species]
        ?? (species === selectedSpeciesKey ? sfrMatrix : null)
        ?? loadCachedMatrix(presetKey)
        ?? getDefaultMatrixForSpecies(presetKey)

      nextMap.set(species, matrix)
    }

    farms.forEach((farm) => {
      const farmSpecies = resolveFarmProductionSpeciesKey(farm)
      if (farmSpecies) {
        ensureSpeciesMatrix(farmSpecies)
      }
    })

    ensureSpeciesMatrix(selectedSpeciesKey)
    return nextMap
  }, [farms, prefetchedSfrMatricesBySpecies, selectedSpeciesKey, sfrMatrix])

  const sfrProductionUnitLookup = useMemo(() => {
    const nextMap = new Map<string, ProductionUnit>()

    farms.forEach((farm) => {
      const farmKeys = [resolveFarmKey(farm), toKey(farm.id), farm.name ?? ''].filter(Boolean)

      ;(farm.productionUnits ?? []).forEach((unit) => {
        const unitKeys = [resolveProductionUnitKey(unit), toKey(unit.id), unit.name ?? ''].filter(Boolean)

        farmKeys.forEach((farmKey) => {
          unitKeys.forEach((unitKey) => {
            nextMap.set(`${farmKey}::${unitKey}`, unit)
          })
        })
      })
    })

    return nextMap
  }, [farms])

  const sfrLotLookup = useMemo(() => {
    const nextMap = new Map<string, Lot>()

    lots.forEach((lot) => {
      const farmKey = toKey(lot.farmId)
      const productionUnitKey = toKey(lot.productionUnitId)
      if (!farmKey || !productionUnitKey) {
        return
      }

      const lotKeys = [
        resolveCanonicalLotKey(lot.id),
        resolveCanonicalLotKey(lot.lotKey)
      ].filter((lotKey) => lotKey && lotKey !== 'unknown-lot')

      lotKeys.forEach((lotKey) => {
        nextMap.set(`${farmKey}::${productionUnitKey}::${lotKey}`, lot)
      })
    })

    return nextMap
  }, [lots])

  const resolveDashboardSfrSpecies = useCallback((row: DatabaseRowWithKey): ProductionSpeciesKey => {
    const farmKey = toKey(row._canonicalFarmKey)
    const farm = (farmKey ? farmMap.get(farmKey) : null) ?? selectedFarm
    return resolveFarmProductionSpeciesKey(farm) ?? selectedSpeciesKey
  }, [farmMap, selectedFarm, selectedSpeciesKey])

  const resolveDashboardSfrMatrix = useCallback((context: {
    group: CanonicalLotGroup
    currentRow: DatabaseRowWithKey
  }): MatrixData => {
    const farmKey = toKey(context.currentRow._canonicalFarmKey)
    const farm = (farmKey ? farmMap.get(farmKey) : null) ?? selectedFarm
    const resolvedFarmKey = farm ? (resolveFarmKey(farm) || toKey(farm.id)) : farmKey
    const productionUnitKey = toKey(context.currentRow._canonicalProductionUnitKey) || context.group.canonicalProductionUnitKey
    const productionUnit = farm && productionUnitKey
      ? sfrProductionUnitLookup.get(`${resolvedFarmKey}::${productionUnitKey}`)
        ?? sfrProductionUnitLookup.get(`${toKey(farm.id)}::${productionUnitKey}`)
        ?? null
      : null
    const lotKey = resolveDashboardRowLotKey({
      lotId: context.currentRow.lotId,
      canonicalLotKey: context.group.canonicalLotKey,
      lot: context.currentRow.lot,
      id: context.currentRow.id
    })
    const lot = resolvedFarmKey && productionUnitKey && lotKey && lotKey !== 'unknown-lot'
      ? sfrLotLookup.get(`${resolvedFarmKey}::${productionUnitKey}::${lotKey}`)
        ?? sfrLotLookup.get(`${toKey(farm?.id)}::${productionUnitKey}::${lotKey}`)
        ?? null
      : null
    const species = resolveDashboardSfrSpecies(context.currentRow)

    return resolveEffectiveSfrMatrix({
      farm: farm ?? undefined,
      productionUnit: productionUnit ?? undefined,
      lot: lot ?? undefined,
      species,
      speciesDefaultMatrix: speciesDefaultMatrixBySpecies.get(species) ?? getDefaultMatrixForSpecies(species)
    })
  }, [farmMap, resolveDashboardSfrSpecies, selectedFarm, sfrLotLookup, sfrProductionUnitLookup, speciesDefaultMatrixBySpecies])

  const selectorSummaryText = useMemo(() => {
    const segments: string[] = []
    segments.push(selectedFarm?.name ?? 'All facilities')
    segments.push(selectedProductionUnitId === ALL_PRODUCTION_UNITS_ID ? 'All units' : (selectedProductionUnit ? selectedProductionUnit.name : 'All units'))
    segments.push(selectedLot ? (selectedLot.lotKey || `Lot ${selectedLot.id}`) : 'All lots')
    return segments.join(' · ')
  }, [selectedFarm, selectedLot, selectedProductionUnit, selectedProductionUnitId])

  const parseFishStock = useCallback((input: string | number | null | undefined) => {
    if (typeof input === 'number') {
      return Number.isFinite(input) ? input : 0
    }
    if (typeof input === 'string') {
      const normalized = input.replace(/[^\d-]/g, '')
      const parsed = Number.parseInt(normalized, 10)
      return Number.isFinite(parsed) ? parsed : 0
    }
    return 0
  }, [])

  const parseWeight = useCallback((input: string | number | null | undefined) => {
    if (typeof input === 'number') {
      return Number.isFinite(input) ? input : 0
    }

    if (typeof input === 'string') {
      const trimmed = input.trim()
      if (trimmed.length === 0) {
        return 0
      }

      let sanitized = trimmed
        .replace(/[^0-9.,-\s]/g, '')
        .replace(/\s+/g, '')

      if (sanitized.length === 0) {
        return 0
      }

      const lastComma = sanitized.lastIndexOf(',')
      const lastDot = sanitized.lastIndexOf('.')

      if (lastComma !== -1 || lastDot !== -1) {
        let decimalSeparator: ',' | '.' | null = null

        if (lastComma > lastDot) {
          decimalSeparator = ','
        } else if (lastDot > lastComma) {
          decimalSeparator = '.'
        } else {
          decimalSeparator = lastComma !== -1 ? ',' : lastDot !== -1 ? '.' : null
        }

        if (decimalSeparator === ',') {
          sanitized = sanitized.replace(/\./g, '')
          sanitized = sanitized.replace(/,/g, '.')
        } else if (decimalSeparator === '.') {
          sanitized = sanitized.replace(/,/g, '')
        }
      } else {
        sanitized = sanitized.replace(/[.,]/g, '')
      }

      sanitized = sanitized
        .replace(/(?!^)-/g, '')
        .replace(/\.(?=.*\.)/g, '')

      const parsed = Number.parseFloat(sanitized)
      return Number.isFinite(parsed) ? parsed : 0
    }

    if (input == null) return 0

    const parsed = Number.parseFloat(String(input))
    return Number.isFinite(parsed) ? parsed : 0
  }, [])

  const resolveLotDate = useCallback((lot: Lot): Date | null => {
    // v2.2 T3-A-01 (2026-07-06): `lot.recordDate` retired as part of the
    // Lot biology cache field retirement. `stockingDate` / `updatedAt` /
    // `createdAt` / `harvestDate` are the remaining Lot lifecycle
    // candidates for date resolution.
    const candidates = [
      lot.stockingDate,
      lot.updatedAt,
      lot.createdAt,
      lot.harvestDate
    ]

    for (const candidate of candidates) {
      if (!candidate) continue
      const date = new Date(candidate)
      if (!Number.isNaN(date.getTime())) {
        return date
      }
    }

    return null
  }, [])

  const getWeekKey = useCallback((value: Date | string | null | undefined) => (
    getWeekKeyFromKpis(value)
  ), [])

  type LotEntryRequirements = {
    requireWeight?: boolean
    requireStock?: boolean
  }

  type LotTimelineEntry = {
    row: DatabaseRowWithKey
    index: number
    date: Date
    weight: number
    stock: number
    rawWeight: string
    rawStock: string
    source: DataRowSource
  }

  const isStageEligibleForMetrics = useCallback((
    row: DatabaseRowWithKey,
    weight: number,
    stock: number
  ) => {
    if (row.stage === 'Active') {
      return true
    }

    if (row.stage === 'Inactive' && row._source === 'local') {
      return weight > 0 && stock > 0
    }

    return false
  }, [])

  const findLatestEntryForWeek = useCallback((
    rows: DatabaseRowWithKey[],
    weekKey: string | null,
    requirements: LotEntryRequirements = { requireStock: true }
  ): LotTimelineEntry | null => {
    if (!weekKey) return null
    let best: LotTimelineEntry | null = null
    const requireWeight = requirements.requireWeight ?? false
    const requireStock = requirements.requireStock ?? true

    rows.forEach((row, index) => {
      if (!row.date) return
      const rowWeekKey = getWeekKey(row.date)
      if (rowWeekKey !== weekKey) return
      const date = new Date(row.date)
      if (Number.isNaN(date.getTime())) return

      const weight = parseWeight(row.weight)
      const stock = parseFishStock(row.fishStock)
      const rawWeight = typeof row.weight === 'string' ? row.weight : row.weight != null ? String(row.weight) : ''
      const rawStock = typeof row.fishStock === 'string' ? row.fishStock : row.fishStock != null ? String(row.fishStock) : ''

      if (!isStageEligibleForMetrics(row, weight, stock)) return

      if (requireWeight && weight <= 0) return
      if (requireStock && stock <= 0) return

      if (!best || date.getTime() > best.date.getTime()) {
        best = {
          row,
          index,
          date,
          weight,
          stock,
          rawWeight,
          rawStock,
          source: row._source
        }
      }
    })

    return best
  }, [getWeekKey, isStageEligibleForMetrics, parseFishStock, parseWeight])

  const findClosestEntryForWeek = useCallback((
    rows: DatabaseRowWithKey[],
    weekKey: string | null,
    referenceDate: Date,
    requirements: LotEntryRequirements = { requireStock: true }
  ): LotTimelineEntry | null => {
    if (!weekKey) return null
    let best: LotTimelineEntry | null = null
    const requireWeight = requirements.requireWeight ?? false
    const requireStock = requirements.requireStock ?? true

    rows.forEach((row, index) => {
      if (!row.date) return
      const rowWeekKey = getWeekKey(row.date)
      if (rowWeekKey !== weekKey) return
      const date = new Date(row.date)
      if (Number.isNaN(date.getTime())) return

      const weight = parseWeight(row.weight)
      const stock = parseFishStock(row.fishStock)
      const rawWeight = typeof row.weight === 'string' ? row.weight : row.weight != null ? String(row.weight) : ''
      const rawStock = typeof row.fishStock === 'string' ? row.fishStock : row.fishStock != null ? String(row.fishStock) : ''

      if (!isStageEligibleForMetrics(row, weight, stock)) return

      if (requireWeight && weight <= 0) return
      if (requireStock && stock <= 0) return

      if (!best) {
        best = {
          row,
          index,
          date,
          weight,
          stock,
          rawWeight,
          rawStock,
          source: row._source
        }
        return
      }

      const candidateDiff = Math.abs(date.getTime() - referenceDate.getTime())
      const bestDiff = Math.abs(best.date.getTime() - referenceDate.getTime())

      if (candidateDiff < bestDiff || (candidateDiff === bestDiff && date.getTime() > best.date.getTime())) {
        best = {
          row,
          index,
          date,
          weight,
          stock,
          rawWeight,
          rawStock,
          source: row._source
        }
      }
    })

    return best
  }, [getWeekKey, isStageEligibleForMetrics, parseFishStock, parseWeight])

  const findPreviousValidEntry = useCallback((
    rows: DatabaseRowWithKey[],
    startIndex: number,
    options?: {
      excludeWeekKey?: string | null
      requirements?: LotEntryRequirements
    }
  ): LotTimelineEntry | null => {
    const requireWeight = options?.requirements?.requireWeight ?? false
    const requireStock = options?.requirements?.requireStock ?? true

    for (let idx = startIndex - 1; idx >= 0; idx--) {
      const candidate = rows[idx]
      if (!candidate?.date) continue
      const candidateDate = new Date(candidate.date)
      if (Number.isNaN(candidateDate.getTime())) continue

      const candidateWeekKey = getWeekKey(candidate.date)
      if (options?.excludeWeekKey && candidateWeekKey === options.excludeWeekKey) {
        continue
      }

      const weight = parseWeight(candidate.weight)
      const stock = parseFishStock(candidate.fishStock)
      const rawWeight = typeof candidate.weight === 'string'
        ? candidate.weight
        : candidate.weight != null
          ? String(candidate.weight)
          : ''
      const rawStock = typeof candidate.fishStock === 'string'
        ? candidate.fishStock
        : candidate.fishStock != null
          ? String(candidate.fishStock)
          : ''

      if (!isStageEligibleForMetrics(candidate, weight, stock)) continue

      if (requireWeight && weight <= 0) continue
      if (requireStock && stock <= 0) continue

      return {
        row: candidate,
        index: idx,
        date: candidateDate,
        weight,
        stock,
        rawWeight,
        rawStock,
        source: candidate._source
      }
    }

    return null
  }, [getWeekKey, isStageEligibleForMetrics, parseFishStock, parseWeight])

  const debugLogCanonicalBucket = useCallback((
    group: CanonicalLotGroup,
    rows: DatabaseRowWithKey[],
    context: { displayLabel: string; lotLabel: string }
  ) => {
    if (!import.meta.env?.DEV) return

    const snapshot = rows
      .slice()
      .sort((a, b) => {
        const aTime = a.date ? new Date(a.date).getTime() : 0
        const bTime = b.date ? new Date(b.date).getTime() : 0
        return aTime - bTime
      })
      .map((row) => ({
        id: row.id,
        date: row.date,
        stage: row.stage,
        weight: row.weight,
        fishStock: row.fishStock,
        canonicalProductionUnit: row._canonicalProductionUnitKey,
        canonicalLot: resolveDashboardRowLotKey(row),
        farmKey: row._farmKey,
        source: row._source
      }))

    console.debug('[growth] unmatched canonical bucket', {
      bucketKey: `${group.canonicalProductionUnitKey ?? 'unknown-pu'}::${group.canonicalLotKey ?? 'unknown-lot'}`,
      displayLabel: context.displayLabel,
      lotLabel: context.lotLabel,
      rowCount: rows.length,
      snapshot
    })
  }, [])

  const logSkippedGrowthLot = useCallback((
    reason: string,
    group: CanonicalLotGroup,
    rows: DatabaseRowWithKey[],
    context: { displayLabel: string; lotLabel: string },
    details?: Record<string, unknown>
  ) => {
    if (!import.meta.env?.DEV) return
    console.debug('[growth] skipped lot', {
      reason,
      ...context,
      ...details
    })
    debugLogCanonicalBucket(group, rows, context)
  }, [debugLogCanonicalBucket])

  const logSkippedMortalityLot = useCallback((
    reason: string,
    context: { displayLabel: string; lotLabel: string },
    details?: Record<string, unknown>
  ) => {
    if (!import.meta.env?.DEV) return
    console.debug('[mortality] skipped lot', {
      reason,
      ...context,
      ...details
    })
  }, [])

  const {
    rowsByDisplayKey: allProductionRows,
    warnings: normalizationWarnings,
    canonicalDisplayLabels: normalizationCanonicalDisplayLabels,
    resolvedAliases: normalizationResolvedAliases,
    unresolvedAliasKeys: normalizationUnresolvedAliasKeys
  } = useMemo<NormalizationResult>(() => {
    type GroupBucket = {
      aliasKeys: Set<string>
      labels: Set<string>
      rows: DatabaseRowWithKey[]
      dedupeIndex: Map<string, number>
      canonicalFarmKey?: string
      canonicalProductionUnitKey?: string
      canonicalDisplayLabel?: string
      sources: Set<DataRowSource>
    }

    const localTableData = productionTableSnapshot
    const warnings: NormalizationIssue[] = []
    const canonicalDisplayLabels = new Map<string, string>()

    const aliasMap = new Map<string, ProductionUnitResolution[]>()
    const canonicalMap = new Map<string, ProductionUnitResolution>()

    const registerAlias = (alias: string | null | undefined, entry: ProductionUnitResolution) => {
      if (!alias) return
      const normalized = canonicalizeLabel(alias)
      if (!normalized) return
      entry.aliasKeys.add(normalized)
      const existing = aliasMap.get(normalized)
      if (existing) {
        if (!existing.some((candidate) => candidate.canonicalGroupKey === entry.canonicalGroupKey)) {
          existing.push(entry)
        }
      } else {
        aliasMap.set(normalized, [entry])
      }
    }

    farms.forEach((farm) => {
      const farmKey = resolveFarmKey(farm)
      const farmIdKey = toKey(farm.id)
      const farmUnits = farm.productionUnits ?? []

      farmUnits.forEach((unit) => {
        const unitKey = resolveProductionUnitKey(unit)
        const unitIdKey = toKey(unit.id)
        const canonicalGroupKey = `${farmKey}::${unitKey}`
        const storageKey = `${farm.name}-${unit.name}`
        const canonicalDisplayLabel = `${farm.name} · ${unit.name}`
        const entry: ProductionUnitResolution = {
          farmId: farmKey,
          farmName: farm.name,
          productionUnitId: unitKey,
          productionUnitName: unit.name,
          canonicalGroupKey,
          storageKey,
          canonicalDisplayLabel,
          aliasKeys: new Set()
        }
        canonicalMap.set(canonicalGroupKey, entry)

        const aliasCandidates = [
          canonicalizeKey(farm.name, unit.name),
          `${farm.name}-${unit.name}`,
          `${farm.name} ${unit.name}`,
          `${farm.name}|${unit.name}`,
          `${unit.name}`,
          `${farmIdKey}-${unitIdKey}`,
          `${farmIdKey}_${unitIdKey}`,
          `${farmIdKey}${unitIdKey}`,
          unitIdKey,
          storageKey
        ]

        aliasCandidates.forEach((candidate) => registerAlias(candidate, entry))
      })
    })

    const groups = new Map<string, GroupBucket>()

    const getDayKey = (value: string | null | undefined) => {
      if (!value) return null
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return null
      return date.toISOString().slice(0, 10)
    }

    const ensureGroup = (groupKey: string, label: string, row: DatabaseRowWithKey) => {
      const safeKey = groupKey || 'unresolved-group'
      if (!groups.has(safeKey)) {
        groups.set(safeKey, {
          aliasKeys: new Set(),
          labels: new Set(),
          rows: [],
          dedupeIndex: new Map(),
          sources: new Set()
        })
      }
      const bucket = groups.get(safeKey)!
      bucket.aliasKeys.add(row._farmKey)
      bucket.labels.add(label)
      bucket.sources.add(row._source)
      if (row._canonicalFarmKey) bucket.canonicalFarmKey = row._canonicalFarmKey
      if (row._canonicalProductionUnitKey) bucket.canonicalProductionUnitKey = row._canonicalProductionUnitKey
      if (row._displayFarmLabel) bucket.canonicalDisplayLabel = row._displayFarmLabel
      return bucket
    }

    const rowQualityScore = (row: DatabaseRowWithKey) => {
      let score = row._source === 'api' ? 6 : 3
      if (row.stage === 'Active') score += 3
      if (parseFishStock(row.fishStock) > 0) score += 2
      if (row.weight && String(row.weight).trim() !== '') score += 1
      if (row.do && String(row.do).trim() !== '') score += 0.5
      return score
    }

    const registerRow = (row: DatabaseRowWithKey) => {
      const groupToken = row._canonicalProductionUnitKey
        || row._canonicalFarmKey
        || canonicalizeKey(row._farmKey)
        || `${row._source}-group`

      const groupLabel = row._displayFarmLabel ?? row._farmKey
      const group = ensureGroup(groupToken, groupLabel, row)

      const dayKey = getDayKey(row.date)
      if (!dayKey) {
        group.rows.push(row)
        return
      }

      const lotToken = resolveDashboardRowLotKey(row)
      const dedupeTokenBase = group.canonicalProductionUnitKey || group.canonicalFarmKey || groupToken
      const dedupeKey = `${dedupeTokenBase}||${lotToken}||${dayKey}`

      if (group.dedupeIndex.has(dedupeKey)) {
        const existingIndex = group.dedupeIndex.get(dedupeKey)!
        const existingRow = group.rows[existingIndex]
        const existingScore = rowQualityScore(existingRow)
        const candidateScore = rowQualityScore(row)

        if (
          candidateScore > existingScore
          || (
            candidateScore === existingScore
            && new Date(row.date).getTime() > new Date(existingRow.date).getTime()
          )
        ) {
          group.rows[existingIndex] = row
        }
        return
      }

      group.dedupeIndex.set(dedupeKey, group.rows.length)
      group.rows.push(row)
    }

    const resolveAlias = (rawKey: string): ProductionUnitResolution | null => {
      const candidateEntries = new Map<string, { entry: ProductionUnitResolution; score: number }>()

      const considerAlias = (value: string | null | undefined, weight = 4) => {
        if (!value) return
        const normalized = canonicalizeLabel(value)
        if (!normalized) return
        const matches = aliasMap.get(normalized)
        if (!matches) return
        matches.forEach((entry) => {
          const current = candidateEntries.get(entry.canonicalGroupKey)
          const existingScore = current?.score ?? 0
          const nextScore = existingScore + weight
          candidateEntries.set(entry.canonicalGroupKey, { entry, score: nextScore })
        })
      }

      considerAlias(rawKey, 6)
      considerAlias(rawKey.replace(/[·|]/g, '-'), 5)
      considerAlias(rawKey.replace(/[_]/g, '-'), 4)
      considerAlias(rawKey.replace(/[^a-zA-Z0-9]+/g, ' '), 3)

      rawKey.split(/[-·|]/).forEach((segment) => {
        considerAlias(segment, 2)
      })

      if (candidateEntries.size === 0) {
        const normalizedRaw = canonicalizeLabel(rawKey)
        if (normalizedRaw) {
          aliasMap.forEach((entries, aliasKey) => {
            const maxLength = Math.max(aliasKey.length, normalizedRaw.length)
            const distance = levenshteinDistance(aliasKey, normalizedRaw)
            const threshold = maxLength <= 5 ? 1 : 2
            if (distance === 0 || distance > threshold) return

            const boost = distance === 1 ? 1.5 : 0.75

            entries.forEach((entry) => {
              const current = candidateEntries.get(entry.canonicalGroupKey)
              const existingScore = current?.score ?? 0
              const nextScore = existingScore + boost
              candidateEntries.set(entry.canonicalGroupKey, { entry, score: nextScore })
            })
          })
        }
      }

      if (candidateEntries.size === 0) {
        return null
      }

      const rawLower = rawKey.toLowerCase()
      candidateEntries.forEach((payload, key) => {
        let score = payload.score
        const { entry } = payload
        if (rawLower.includes(entry.farmName.toLowerCase())) score += 1.5
        if (rawLower.includes(entry.productionUnitName.toLowerCase())) score += 2
        if (rawLower.includes(entry.productionUnitId.toLowerCase())) score += 2.5
        candidateEntries.set(key, { entry, score })
      })

      const ranked = Array.from(candidateEntries.values())
        .sort((a, b) => b.score - a.score)

      if (ranked.length === 0) {
        return null
      }

      if (ranked.length === 1) {
        return ranked[0].entry
      }

      if (ranked[0].score === ranked[1].score) {
        warnings.push({
          type: 'ambiguous-alias',
          alias: rawKey,
          candidates: ranked.slice(0, 3).map((item) => item.entry.canonicalDisplayLabel)
        })
        return null
      }

      return ranked[0].entry
    }

    const rewriteBuckets = new Map<string, { canonicalKey: string; resolution: ProductionUnitResolution; rows: Map<string, any> }>()
    const unresolvedAliases = new Set<string>()
    const resolvedAliasRecords = new Map<string, ResolvedAliasRecord>()

    Object.entries(localTableData).forEach(([legacyKey, value]) => {
      if (!Array.isArray(value)) return

      const rawFarmKey = toKey((value[0] as any)?.farmId)
      const rawProductionUnitKey = toKey((value[0] as any)?.productionUnitId)

      const resolution = (rawFarmKey && rawProductionUnitKey
        ? canonicalMap.get(`${rawFarmKey}::${rawProductionUnitKey}`) ?? null
        : null) ?? resolveAlias(legacyKey)
      if (!resolution) {
        unresolvedAliases.add(legacyKey)
      } else {
        resolvedAliasRecords.set(legacyKey, {
          aliasKey: legacyKey,
          displayLabel: resolution.canonicalDisplayLabel,
          canonicalFarmId: resolution.farmId,
          canonicalProductionUnitId: resolution.productionUnitId
        })
      }

      value.forEach((rawRow: any) => {
        if (!rawRow) return

        const rawDate: string | null = typeof rawRow.date === 'string' ? rawRow.date : null
        const date = rawDate ? new Date(rawDate) : null
        if (!date || Number.isNaN(date.getTime())) return

        const id = typeof rawRow.id === 'string' && rawRow.id.trim() !== ''
          ? rawRow.id.trim()
          : `${legacyKey}-${String(rawRow.lot ?? 'lot')}-${date.getTime()}`

        const resolvedLot = typeof rawRow.lot === 'string' && rawRow.lot.trim() !== ''
          ? rawRow.lot.trim()
          : (typeof rawRow.lotId === 'string' && rawRow.lotId.trim() !== '' ? rawRow.lotId.trim() : id)
        const canonicalLotKey = resolveDashboardRowLotKey({
          lotId: rawRow.lotId as string | null | undefined,
          canonicalLotKey: rawRow.canonicalLotKey as string | null | undefined,
          _canonicalLotKey: rawRow._canonicalLotKey as string | null | undefined,
          lot: resolvedLot,
          id
        })

        const rowFarmKey = toKey(rawRow.farmId)
        const rowProductionUnitKey = toKey(rawRow.productionUnitId)
        const canonicalFarmKey = toKey(resolution?.farmId) || rowFarmKey || canonicalizeKey(legacyKey) || 'legacy-farm'
        const canonicalProductionUnitKey = toKey(resolution?.productionUnitId) || rowProductionUnitKey

        const normalizedRow: DatabaseRowWithKey = {
          id,
          lotId: typeof rawRow.lotId === 'string' ? rawRow.lotId : undefined,
          lot: resolvedLot,
          stage: normalizeStage(rawRow.stage),
          date: date.toISOString(),
          weight: typeof rawRow.weight === 'string' ? rawRow.weight : rawRow.weight != null ? String(rawRow.weight) : '',
          upVol: typeof rawRow.upVol === 'string' ? rawRow.upVol : rawRow.upVol != null ? String(rawRow.upVol) : '',
          mort: typeof rawRow.mort === 'string' ? rawRow.mort : rawRow.mort != null ? String(rawRow.mort) : '',
          fishStock: typeof rawRow.fishStock === 'string'
            ? rawRow.fishStock
            : rawRow.fishStock != null
              ? String(rawRow.fishStock)
              : '',
          do: typeof rawRow.do === 'string'
            ? rawRow.do
            : typeof rawRow.dissolvedOxygen === 'string'
              ? rawRow.dissolvedOxygen
              : '',
          _farmKey: legacyKey,
          _canonicalFarmKey: canonicalFarmKey,
          _canonicalProductionUnitKey: canonicalProductionUnitKey,
          _canonicalLotKey: canonicalLotKey,
          _source: 'local',
          _displayFarmLabel: resolution?.canonicalDisplayLabel
        }

        registerRow(normalizedRow)

        if (resolution) {
          const canonicalKey = resolution.storageKey
          const persistedRow = {
            ...rawRow,
            id,
            lot: normalizedRow.lot,
            stage: normalizedRow.stage,
            date: normalizedRow.date,
            weight: normalizedRow.weight,
            upVol: normalizedRow.upVol,
            mort: normalizedRow.mort,
            fishStock: normalizedRow.fishStock,
            do: normalizedRow.do
          }

          let bucket = rewriteBuckets.get(legacyKey)
          if (!bucket) {
            bucket = {
              canonicalKey,
              resolution,
              rows: new Map()
            }
            rewriteBuckets.set(legacyKey, bucket)
          }

          bucket.rows.set(id, persistedRow)
        }
      })
    })

    lots.forEach((lot) => {
      const lotDate = resolveLotDate(lot)
      if (!lotDate) return

      const lotProductionUnitKey = toKey(lot.productionUnitId)
      const mapping = productionUnitLookup[lotProductionUnitKey]
      const farmKey = mapping?.farmId ?? toKey(lot.farmId)
      const farmName = mapping?.farmName ?? farmMap.get(farmKey)?.name ?? 'Unknown Facility'
      const productionUnitName = mapping?.productionUnitName ?? lot.productionUnitId
      const canonicalGroupKey = farmKey ? `${farmKey}::${lotProductionUnitKey}` : null

      let resolution: ProductionUnitResolution | undefined
      if (canonicalGroupKey) {
        resolution = canonicalMap.get(canonicalGroupKey)
      }

      if (!resolution && farmKey) {
        const storageKey = `${farmName}-${productionUnitName}`
        const canonicalDisplayLabel = `${farmName} · ${productionUnitName}`
        resolution = {
          farmId: farmKey,
          farmName,
          productionUnitId: lotProductionUnitKey,
          productionUnitName,
          canonicalGroupKey: canonicalGroupKey ?? `${farmKey}::${lotProductionUnitKey}`,
          storageKey,
          canonicalDisplayLabel,
          aliasKeys: new Set()
        }
        canonicalMap.set(resolution.canonicalGroupKey, resolution)
      }

      const rowStage: DatabaseRowWithKey['stage'] = normalizeStage(lot.stage)

      const displayLabel = resolution?.canonicalDisplayLabel ?? `${farmName} · ${productionUnitName}`
      const fallbackFarmKey = resolution?.storageKey ?? `${farmName}-${productionUnitName}`
      const canonicalFarmKey = toKey(resolution?.farmId) || canonicalizeKey(fallbackFarmKey) || 'api-farm-unknown'
      const canonicalProductionUnitKey = toKey(resolution?.productionUnitId) || lotProductionUnitKey

      // v2.2 T3-A-01 (2026-07-06): the biology-cache fields on Lot
      // (`weight`, `fishStock`, `upVol`, `mort`, `dissolvedOxygen`) are
      // retired. Synthesized-from-Lot rows now carry canonical fields
      // only (id, lot, stage, date, farm/PU keys); biology fields default
      // to empty. Consumers downstream (Production section weight chart)
      // already filter on `weightG > 0`, so no visible artifact.
      const row: DatabaseRowWithKey = {
        id: resolveDashboardSelectionLotKey(lot) || lot.id,
        lotId: toKey(lot.id) || undefined,
        lot: lot.lotKey || lot.id,
        stage: rowStage,
        date: lotDate.toISOString(),
        weight: '',
        upVol: '',
        mort: '',
        fishStock: '',
        do: '',
        _farmKey: fallbackFarmKey,
        _canonicalFarmKey: canonicalFarmKey,
        _canonicalProductionUnitKey: canonicalProductionUnitKey,
        _canonicalLotKey: resolveDashboardRowLotKey({ lotId: lot.id, lot: lot.lotKey }),
        _source: 'api',
        _displayFarmLabel: displayLabel
      }

      registerRow(row)
    })

    const rowsByDisplayKey: Record<string, DatabaseRowWithKey[]> = {}
    const collisionCounter = new Map<string, number>()

    groups.forEach((group) => {
      const baseLabel = group.canonicalDisplayLabel ?? selectDisplayLabel(group.labels)

      if (baseLabel) {
        if (group.canonicalProductionUnitKey) {
          canonicalDisplayLabels.set(group.canonicalProductionUnitKey, baseLabel)
        } else if (group.canonicalFarmKey) {
          canonicalDisplayLabels.set(group.canonicalFarmKey, baseLabel)
        }
      }

      let displayLabel = baseLabel
      let suffix = collisionCounter.get(baseLabel) ?? 0

      while (rowsByDisplayKey[displayLabel]) {
        suffix += 1
        displayLabel = `${baseLabel} (${suffix})`
      }

      collisionCounter.set(baseLabel, suffix)

      rowsByDisplayKey[displayLabel] = group.rows.map((row) => ({
        ...row,
        _farmKey: displayLabel
      }))
    })

    const unresolvedKeys = Array.from(unresolvedAliases)
    unresolvedKeys.forEach((alias) => {
      warnings.push({ type: 'unresolved-alias', alias })
    })

    let rewritePlan: LegacyRewritePlan | null = null

    if (rewriteBuckets.size > 0) {
      const updatedData: Record<string, unknown> = { ...localTableData }
      let changed = false
      const removedLegacyKeys: string[] = []
      const migratedCanonicalKeys = new Set<string>()

      rewriteBuckets.forEach((bucket, legacyKey) => {
        const canonicalKey = bucket.canonicalKey
        const existing = Array.isArray(localTableData[canonicalKey])
          ? (localTableData[canonicalKey] as any[])
          : []

        const mergedMap = new Map<string, any>()

        existing.forEach((row: any) => {
          if (!row) return
          const rowId = typeof row.id === 'string' && row.id !== ''
            ? row.id
            : `${row.lot ?? 'lot'}-${row.date ?? ''}`
          mergedMap.set(rowId, row)
        })

        bucket.rows.forEach((row, rowId) => {
          mergedMap.set(rowId, row)
        })

        const mergedRows = Array.from(mergedMap.values())
        const existingJson = JSON.stringify(existing)
        const mergedJson = JSON.stringify(mergedRows)
        if (existingJson !== mergedJson) {
          changed = true
        }

        updatedData[canonicalKey] = mergedRows
        migratedCanonicalKeys.add(canonicalKey)

        if (canonicalKey !== legacyKey) {
          removedLegacyKeys.push(legacyKey)
          delete updatedData[legacyKey]
          changed = true
        }
      })

      if (changed) {
        rewritePlan = {
          shouldRewrite: true,
          updatedData,
          originalData: localTableData,
          backupKey: `productionUnitTableData-backup-${Date.now()}`,
          summary: {
            migratedKeys: migratedCanonicalKeys.size,
            removedLegacyKeys,
            unresolvedKeys
          }
        }
      }
    }

    return {
      rowsByDisplayKey,
      warnings,
      rewritePlan,
      canonicalDisplayLabels,
      resolvedAliases: Array.from(resolvedAliasRecords.values()),
      unresolvedAliasKeys: unresolvedKeys
    }
  }, [dashboardDataRevision, farms, farmMap, lots, parseFishStock, productionUnitLookup, productionTableSnapshot, resolveLotDate])

  const filteredProductionRows = useMemo(() => {
    const filterByFarm = Boolean(selectedFarmId)
    const filterByUnit = Boolean(selectedProductionUnitId) && selectedProductionUnitId !== ALL_PRODUCTION_UNITS_ID
    const filterByLot = Boolean(selectedLotId)

    if (!filterByFarm && !filterByUnit && !filterByLot) {
      return allProductionRows
    }

    const entries = Object.entries(allProductionRows)
    const filtered: Record<string, DatabaseRowWithKey[]> = {}

    entries.forEach(([label, rows]) => {
      const matchingRows = rows.filter((row) => {
        const rowFarmKey = toKey(row._canonicalFarmKey)
        if (filterByFarm && rowFarmKey !== selectedFarmId) return false

        const rowProductionUnitKey = toKey(row._canonicalProductionUnitKey)
        if (filterByUnit && rowProductionUnitKey !== selectedProductionUnitId) return false

        if (filterByLot) {
          const rowLotKey = resolveDashboardRowLotKey(row)
          if (!selectedLotTokens.has(rowLotKey)) {
            return false
          }
        }
        return true
      })

      if (matchingRows.length > 0) {
        filtered[label] = matchingRows
      }
    })

    return filtered
  }, [allProductionRows, selectedFarmId, selectedLotId, selectedLotTokens, selectedProductionUnitId])

  const filteredRowCount = useMemo(() => (
    Object.values(filteredProductionRows).reduce((sum, rows) => sum + rows.length, 0)
  ), [filteredProductionRows])

  const flatFilteredProductionRows = useMemo(
    () => Object.values(filteredProductionRows).flat(),
    [filteredProductionRows]
  )

  const canonicalLotGroups = useMemo((): CanonicalLotGroup[] => {
    if (loadingData) return []
    if (flatFilteredProductionRows.length === 0) return []
    return buildCanonicalLotGroups(flatFilteredProductionRows, normalizationCanonicalDisplayLabels)
  }, [flatFilteredProductionRows, loadingData, normalizationCanonicalDisplayLabels])

  const aliasSyncStateRef = useRef<{
    resolved: Map<string, string>
    unresolved: Set<string>
    inFlightResolved: Set<string>
    inFlightUnresolved: Set<string>
  }>({
    resolved: new Map<string, string>(),
    unresolved: new Set<string>(),
    inFlightResolved: new Set<string>(),
    inFlightUnresolved: new Set<string>()
  })
  const aliasSyncRunIdRef = useRef(0)

  useEffect(() => {
    if (!normalizationWarnings.length) return

    const unresolved = normalizationWarnings.filter((warning) => warning.type === 'unresolved-alias')
    const ambiguous = normalizationWarnings.filter((warning) => warning.type === 'ambiguous-alias')

    if (unresolved.length > 0) {
      console.warn('[Dashboard] Unresolved production unit aliases detected', unresolved)
    }

    if (ambiguous.length > 0) {
      console.warn('[Dashboard] Ambiguous production unit aliases detected', ambiguous)
    }
  }, [normalizationWarnings])

  useEffect(() => {
    if (!normalizationResolvedAliases.length && !normalizationUnresolvedAliasKeys.length) return

    console.debug('[Dashboard] Alias resolution snapshot', {
      resolved: normalizationResolvedAliases,
      unresolved: normalizationUnresolvedAliasKeys
    })
  }, [normalizationResolvedAliases, normalizationUnresolvedAliasKeys])

  useEffect(() => {
    if (loadingData) return

    const state = aliasSyncStateRef.current
    const resolvedSignatures = new Map<string, string>()

    const pendingResolved = normalizationResolvedAliases.filter((record) => {
      if (!record.canonicalFarmId || !record.canonicalProductionUnitId) {
        return false
      }
      if (state.inFlightResolved.has(record.aliasKey)) {
        return false
      }
      const signature = `${record.canonicalFarmId}::${record.canonicalProductionUnitId}::${record.displayLabel ?? ''}`
      resolvedSignatures.set(record.aliasKey, signature)
      const previousSignature = state.resolved.get(record.aliasKey)
      return previousSignature !== signature
    })

    const pendingUnresolved = normalizationUnresolvedAliasKeys.filter((aliasKey) => {
      if (!aliasKey || typeof aliasKey !== 'string') return false
      if (resolvedSignatures.has(aliasKey)) return false
      if (state.inFlightUnresolved.has(aliasKey)) return false
      return !state.unresolved.has(aliasKey)
    })

    if (!pendingResolved.length && !pendingUnresolved.length) {
      return
    }

    pendingResolved.forEach((record) => state.inFlightResolved.add(record.aliasKey))
    pendingUnresolved.forEach((aliasKey) => state.inFlightUnresolved.add(aliasKey))

    let cancelled = false
    const runId = aliasSyncRunIdRef.current + 1
    aliasSyncRunIdRef.current = runId

    const sync = async () => {
      const resolvedUpdates: Array<[string, string]> = []
      const unresolvedUpdates: string[] = []

      for (const aliasKey of pendingUnresolved) {
        try {
          await api.productionUnitAliases.register({ aliasKey, displayLabel: aliasKey })
          unresolvedUpdates.push(aliasKey)
        } catch (error) {
          console.error('[Dashboard] Failed to register unresolved alias', aliasKey, error)
        }
      }

      for (const record of pendingResolved) {
        try {
          await api.productionUnitAliases.resolve(record.aliasKey, {
            displayLabel: record.displayLabel,
            canonicalFarmId: record.canonicalFarmId,
            canonicalProductionUnitId: record.canonicalProductionUnitId
          })
          const signature = resolvedSignatures.get(record.aliasKey)
            ?? `${record.canonicalFarmId}::${record.canonicalProductionUnitId}::${record.displayLabel ?? ''}`
          resolvedUpdates.push([record.aliasKey, signature])
        } catch (error) {
          console.error('[Dashboard] Failed to sync resolved alias', record.aliasKey, error)
        }
      }

      const releaseInFlight = () => {
        const nextState = aliasSyncStateRef.current
        pendingUnresolved.forEach((aliasKey) => nextState.inFlightUnresolved.delete(aliasKey))
        pendingResolved.forEach((record) => nextState.inFlightResolved.delete(record.aliasKey))
        return nextState
      }

      if (cancelled || aliasSyncRunIdRef.current !== runId) {
        releaseInFlight()
        return
      }

      const nextState = releaseInFlight()
      unresolvedUpdates.forEach((aliasKey) => nextState.unresolved.add(aliasKey))
      resolvedUpdates.forEach(([aliasKey, signature]) => {
        nextState.resolved.set(aliasKey, signature)
        nextState.unresolved.delete(aliasKey)
      })
    }

    void sync()

    return () => {
      cancelled = true
    }
  }, [loadingData, normalizationResolvedAliases, normalizationUnresolvedAliasKeys])

  useEffect(() => {
    loadData().finally(() => {
      setRefreshTime(new Date())
      // Ensure memoized datasets that depend on storage-backed reads can refresh.
      setDashboardDataRevision((value) => value + 1)
    })
  }, [loadData])

  useEffect(() => {
    let cancelled = false

    const loadTaskSummary = async () => {
      try {
        await ensureOfflineReady()
        const tasks = await dexieTaskRepo.list()
        const totalSummary = summarizeTasksForDashboard(tasks)

        const extractTaskContext = (metadata?: Record<string, unknown>) => {
          if (!metadata) return {}
          const farmValue = metadata['farmId']
          const unitValue = metadata['productionUnitId']
          const lotValue = metadata['lotId']
          return {
            farmId: typeof farmValue === 'string' ? farmValue : undefined,
            productionUnitId: typeof unitValue === 'string' ? unitValue : undefined,
            lotId: typeof lotValue === 'string' ? lotValue : undefined
          }
        }

        const scopedTasks = tasks.filter((task) => {
          if (task.completed) return false
          const context = extractTaskContext(task.metadata)

          if (selectedFarmId && context.farmId && context.farmId !== selectedFarmId) return false
          if (selectedProductionUnitId && context.productionUnitId && context.productionUnitId !== selectedProductionUnitId) return false
          if (
            selectedLotId
            && context.lotId
            && !matchesDashboardLotSelection(selectedLotId, context.lotId, availableLots.length > 0 ? availableLots : lots)
          ) return false

          return true
        })

        const hasAlertTag = (tags?: string[]) =>
          (tags ?? []).some((tag) => tag.trim().toLowerCase() === 'alert')

        const sortByUrgency = (items: Task[]) => items
          .slice()
          .sort((a, b) => {
            const dueA = a.dueAt ? new Date(a.dueAt).getTime() : Number.MAX_SAFE_INTEGER
            const dueB = b.dueAt ? new Date(b.dueAt).getTime() : Number.MAX_SAFE_INTEGER
            if (dueA === dueB) return b.updatedAt.localeCompare(a.updatedAt)
            return dueA - dueB
          })

        const alertTagged = sortByUrgency(scopedTasks.filter((task) => hasAlertTag(task.tags)))
        const fallbackAlerts = sortByUrgency(scopedTasks.filter((task) => {
          const status = classifyTaskStatus(task)
          return status.code === 'overdue' || status.code === 'due_soon'
        }))

        const alertIds = new Set(alertTagged.map((task) => task.id))
        const filledFallback = fallbackAlerts.filter((task) => !alertIds.has(task.id))

        const alertCandidates = alertTagged
          .slice(0, 3)
          .concat(filledFallback.slice(0, Math.max(0, 3 - alertTagged.length)))

        const scopedSummary = summarizeTasksForDashboard(scopedTasks)
        const summary: TaskDashboardSummary = {
          ...scopedSummary,
          updatedAt: totalSummary.updatedAt
        }
        if (cancelled) return
        setTaskDashboardSummary(summary)
        setSystemAlertTasks(alertCandidates)
        setTaskDashboardSummaryError(null)
      } catch (error) {
        if (cancelled) return
        setTaskDashboardSummary(null)
        setSystemAlertTasks([])
        setTaskDashboardSummaryError(error instanceof Error ? error.message : String(error))
      }
    }

    void loadTaskSummary()

    return () => {
      cancelled = true
    }
  }, [availableLots, lots, selectedFarmId, selectedLotId, selectedProductionUnitId])

  useEffect(() => {
    let cancelled = false

    const loadRecentActivity = async () => {
      try {
        const events = await dexieActivityRepo.listRecent(60)
        const matchesSelection = (event: ActivityEvent): boolean => {
          if (selectedFarmId && event.farmId && event.farmId !== selectedFarmId) return false
          if (selectedProductionUnitId && event.productionUnitId && event.productionUnitId !== selectedProductionUnitId) return false
          if (
            selectedLotId
            && event.lotId
            && !matchesDashboardLotSelection(selectedLotId, event.lotId, availableLots.length > 0 ? availableLots : lots)
          ) return false
          return true
        }

        const matching = events.filter(matchesSelection)
        const nonMatching = events.filter((event) => !matchesSelection(event))
        const ordered = matching.concat(nonMatching)

        if (cancelled) return
        setRecentActivity(ordered.slice(0, 10))
        setRecentActivityError(null)
      } catch (error) {
        if (cancelled) return
        setRecentActivity([])
        setRecentActivityError(error instanceof Error ? error.message : String(error))
      }
    }

    void loadRecentActivity()

    const handler = () => {
      void loadRecentActivity()
    }

    if (typeof window !== 'undefined') {
      window.addEventListener(ACTIVITY_EVENTS_UPDATED, handler)
    }

    return () => {
      cancelled = true
      if (typeof window !== 'undefined') {
        window.removeEventListener(ACTIVITY_EVENTS_UPDATED, handler)
      }
    }
  }, [availableLots, lots, selectedFarmId, selectedLotId, selectedProductionUnitId])

  useEffect(() => {
    if (loadingData) return

    const hasRows = Object.values(filteredProductionRows).some((rows) => rows.length > 0)
    if (!hasRows) {
      setTotalFishStock(0)
      setActiveFishRows([])
      setFishStockTrend(0)
      setFishStockDebug(null)
      return
    }

    try {
      const { displayTotal, trendPercent, activeRows, debug } = computeFishStockKpi({
        rowsByDisplayKey: filteredProductionRows
      })
      setTotalFishStock(displayTotal)
      setActiveFishRows(activeRows)
      setFishStockTrend(trendPercent)
      setFishStockDebug(debug)
    } catch {
      setTotalFishStock(0)
      setActiveFishRows([])
      setFishStockTrend(0)
      setFishStockDebug(null)
    }
  }, [filteredProductionRows, loadingData])

  useEffect(() => {
    if (loadingData) return

    if (canonicalLotGroups.length === 0) {
      setKpiData((prev) => ({
        ...prev,
        avgWaterTemp: 0,
        avgWaterTempTrend: 0,
        avgWaterPh: 0,
        avgWaterPhTrend: 0,
        avgWaterDo: 0,
        avgWaterDoTrend: 0,
        avgWaterSalinity: 0,
        avgWaterSalinityTrend: 0
      }))
      setWaterQualityKpis({
        temp: emptyWaterQualityKpi(),
        ph: emptyWaterQualityKpi(),
        dissolvedOxygen: emptyWaterQualityKpi(),
        salinity: emptyWaterQualityKpi()
      })
      return
    }

    try {
      const baseKeys = collectDashboardWaterBaseKeys(canonicalLotGroups)
      const waterRows = loadDashboardWaterRows(baseKeys)

      const tempKpi = computeAvgWaterTempKpi({
        canonicalLotGroups,
        waterRows
      })

      const phKpi = computeAvgWaterMetricKpi({
        canonicalLotGroups,
        waterRows,
        metric: 'ph'
      })

      const dissolvedOxygenKpi = computeAvgWaterMetricKpi({
        canonicalLotGroups,
        waterRows,
        metric: 'dissolvedOxygen'
      })

      const salinityKpi = computeAvgWaterMetricKpi({
        canonicalLotGroups,
        waterRows,
        metric: 'salinity'
      })

      setWaterQualityKpis({
        temp: tempKpi,
        ph: phKpi,
        dissolvedOxygen: dissolvedOxygenKpi,
        salinity: salinityKpi
      })

      setKpiData((prev) => ({
        ...prev,
        avgWaterTemp: tempKpi.current,
        avgWaterTempTrend: tempKpi.previous !== null ? tempKpi.trend : 0,
        avgWaterPh: phKpi.current,
        avgWaterPhTrend: phKpi.previous !== null ? phKpi.trend : 0,
        avgWaterDo: dissolvedOxygenKpi.current,
        avgWaterDoTrend: dissolvedOxygenKpi.previous !== null ? dissolvedOxygenKpi.trend : 0,
        avgWaterSalinity: salinityKpi.current,
        avgWaterSalinityTrend: salinityKpi.previous !== null ? salinityKpi.trend : 0
      }))
    } catch {
      setKpiData((prev) => ({
        ...prev,
        avgWaterTemp: 0,
        avgWaterTempTrend: 0,
        avgWaterPh: 0,
        avgWaterPhTrend: 0,
        avgWaterDo: 0,
        avgWaterDoTrend: 0,
        avgWaterSalinity: 0,
        avgWaterSalinityTrend: 0
      }))
      setWaterQualityKpis({
        temp: emptyWaterQualityKpi(),
        ph: emptyWaterQualityKpi(),
        dissolvedOxygen: emptyWaterQualityKpi(),
        salinity: emptyWaterQualityKpi()
      })
    }
  }, [canonicalLotGroups, dashboardDataRevision, loadingData])

  useEffect(() => {
    if (loadingData) return

    const hasRows = Object.values(filteredProductionRows).some((rows) => rows.length > 0)
    if (!hasRows) {
      setFeedDebugRows([])
      setFeedDebugSummary(null)
      setKpiData((prev) => ({ ...prev, dailyFeed: 0, dailyFeedTrend: 0 }))
      return
    }

    try {
      const baseKeys = collectDashboardWaterBaseKeys(canonicalLotGroups)
      const waterRows = loadDashboardWaterRows(baseKeys)

      const { dailyFeedKg, trendPercent, debugRows, debugSummary, missingPreviousLots } = computeActiveDailyFeedKpi({
        canonicalLotGroups,
        waterRows,
        timeZone: selectedFarmTimeZone,
        species: selectedSfrMatrixKey,
        sfrMatrix,
        resolveSfrMatrix: resolveDashboardSfrMatrix,
        resolveSpecies: ({ currentRow }) => resolveDashboardSfrSpecies(currentRow)
      })

      setFeedDebugRows(debugRows)
      setFeedDebugSummary(debugSummary)
      setKpiData((prev) => ({
        ...prev,
        dailyFeed: dailyFeedKg,
        dailyFeedTrend: trendPercent
      }))

      if (missingPreviousLots.length > 0) {
        console.info('[dashboard] Active feed KPI: missing previous-week rows for lots:', missingPreviousLots.join(', '))
      }
    } catch (error) {
      console.error('Failed to compute Active Daily Feed Consumption KPI', error)
      setFeedDebugRows([])
      setFeedDebugSummary(null)
      setKpiData((prev) => ({
        ...prev,
        dailyFeed: 0,
        dailyFeedTrend: 0
      }))
    }
  }, [
    canonicalLotGroups,
    dashboardDataRevision,
    loadingData,
    resolveDashboardSfrMatrix,
    resolveDashboardSfrSpecies,
    selectedFarmTimeZone,
    selectedSfrMatrixKey,
    sfrMatrix
  ])

  // Actual feed KPI calculation (FeedLog events).
  useEffect(() => {
    let cancelled = false

    const parseDecimal = (value: string | undefined): number => {
      if (!value) return 0
      const trimmed = value.trim()
      if (!trimmed) return 0
      const normalized = trimmed.replace(',', '.')
      const parsed = Number.parseFloat(normalized)
      return Number.isFinite(parsed) ? parsed : 0
    }

    const run = async () => {
      if (loadingData) return
      if (!selectedFarmId || !selectedProductionUnitId) {
        setActualFeedDebugSummary(null)
        setKpiData((prev) => ({
          ...prev,
          actualDailyFeed: 0,
          actualDailyFeedTrend: 0,
          actualDailyFeedEvents: 0
        }))
        return
      }

      const now = new Date()
      const currentWeekKey = feedDebugSummary?.currentWeekKey
        ?? getWeekKeyInTimeZone(now, selectedFarmTimeZone)
        ?? getWeekKey(now)
        ?? undefined
      const previousWeekKey = feedDebugSummary?.previousWeekKey ?? (() => {
        const previous = new Date(now)
        previous.setDate(now.getDate() - 7)
        return getWeekKeyInTimeZone(previous, selectedFarmTimeZone) ?? getWeekKey(previous) ?? undefined
      })()

      try {
        const logs = await feedLogApi.list({ farmId: selectedFarmId, productionUnitId: selectedProductionUnitId })
        if (cancelled) return

        let currentWeeklyKg = 0
        let previousWeeklyKg = 0
        let currentEvents = 0
        let previousEvents = 0

        logs.forEach((log) => {
          const weekKey = getWeekKeyInTimeZone(log.recordedAt, selectedFarmTimeZone) ?? getWeekKey(log.recordedAt)
          if (!weekKey) return
          const kg = parseDecimal(log.feedKg)
          if (kg <= 0) return

          if (currentWeekKey && weekKey === currentWeekKey) {
            currentWeeklyKg += kg
            currentEvents += 1
          }
          if (previousWeekKey && weekKey === previousWeekKey) {
            previousWeeklyKg += kg
            previousEvents += 1
          }
        })

        const currentDaily = currentWeeklyKg / 7
        const previousDaily = previousWeeklyKg / 7
        const trend = previousDaily > 0
          ? ((currentDaily - previousDaily) / Math.max(previousDaily, 1)) * 100
          : 0

        setActualFeedDebugSummary({
          currentWeekKey,
          previousWeekKey,
          currentWeeklyKg,
          previousWeeklyKg,
          currentEvents,
          previousEvents
        })

        setKpiData((prev) => ({
          ...prev,
          actualDailyFeed: Number.isFinite(currentDaily) ? currentDaily : 0,
          actualDailyFeedTrend: Number.isFinite(trend) ? trend : 0,
          actualDailyFeedEvents: currentEvents
        }))
      } catch (error) {
        console.warn('[dashboard] failed to compute actual feed KPI', error)
        if (cancelled) return
        setActualFeedDebugSummary(null)
        setKpiData((prev) => ({
          ...prev,
          actualDailyFeed: 0,
          actualDailyFeedTrend: 0,
          actualDailyFeedEvents: 0
        }))
      }
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [feedDebugSummary?.currentWeekKey, feedDebugSummary?.previousWeekKey, loadingData, selectedFarmId, selectedProductionUnitId, selectedFarmTimeZone])

  // Growth rate KPI calculation
  useEffect(() => {
    if (loadingData) return

    try {
      const { growthRate, growthTrend, debugRows, debugSummary } = computeGrowthRateKpi({
        canonicalLotGroups
      })

      setGrowthDebugRows(debugRows)
      setGrowthDebugSummary(debugSummary)
      setKpiData((prev) => ({
        ...prev,
        growthRate,
        growthTrend
      }))
    } catch (error) {
      console.error('Failed to compute Growth Rate KPI', error)
      setGrowthDebugRows([])
      setGrowthDebugSummary(null)
      setKpiData((prev) => ({
        ...prev,
        growthRate: 0,
        growthTrend: 0
      }))
    }
  }, [canonicalLotGroups, loadingData])

  // Mortality KPI calculation
  useEffect(() => {
    if (loadingData) return

    try {
      const { mortalityRate, mortalityTrend, debugRows, debugSummary } = computeMortalityKpi({
        canonicalLotGroups
      })

      setMortalityDebugRows(debugRows)
      setMortalityDebugSummary(debugSummary)
      setKpiData((prev) => ({
        ...prev,
        mortalityRate,
        mortalityTrend
      }))
    } catch (error) {
      console.error('Failed to compute Mortality KPI', error)
      setMortalityDebugRows([])
      setMortalityDebugSummary(null)
      setKpiData((prev) => ({
        ...prev,
        mortalityRate: 0,
        mortalityTrend: 0
      }))
    }
  }, [canonicalLotGroups, loadingData])

  // Optional: update localStorage when value changes (if you want to allow updates)
  // useEffect(() => {
  //   localStorage.setItem('totalFishStock', String(totalFishStock))
  // }, [totalFishStock])

  useEffect(() => {
    if (!selectedFarmId) {
      setCurrentFarm(null)
      return
    }

    setCurrentFarm(farmMap.get(selectedFarmId) ?? null)
  }, [farmMap, selectedFarmId])

  useEffect(() => {
    if (currentFarm) {
      writeCurrentFarm(currentFarm as any)
    }
  }, [currentFarm])

  const mortalityRateDisplay = Number.isFinite(kpiData.mortalityRate)
    ? kpiData.mortalityRate.toFixed(2)
    : '0.00'

  const mortalityTrendValue = Number.isFinite(kpiData.mortalityTrend)
    ? Number(kpiData.mortalityTrend.toFixed(2))
    : 0

  const mortalityTrendDirection: 'up' | 'down' | 'neutral' = kpiData.mortalityTrend > 0
    ? 'up'
    : kpiData.mortalityTrend < 0
      ? 'down'
      : 'neutral'

  const mortalityRelativeChange = mortalityDebugSummary
    && mortalityDebugSummary.previousRate !== null
    && Number.isFinite(mortalityDebugSummary.previousRate)
    && Math.abs(mortalityDebugSummary.previousRate) > 0
    ? ((mortalityDebugSummary.currentRate - mortalityDebugSummary.previousRate)
      / Math.abs(mortalityDebugSummary.previousRate)) * 100
    : null

  const mortalityTrendDetail = mortalityRelativeChange !== null
    ? `(${mortalityRelativeChange >= 0 ? '+' : ''}${mortalityRelativeChange.toFixed(2)}% relative)`
    : undefined

  const growthRateValue = Number.isFinite(kpiData.growthRate)
    ? Number(kpiData.growthRate.toFixed(2))
    : 0

  const growthTrendValue = Number.isFinite(kpiData.growthTrend)
    ? Number(kpiData.growthTrend.toFixed(2))
    : 0

  const growthTrendDirection: 'up' | 'down' | 'neutral' = kpiData.growthTrend > 0
    ? 'up'
    : kpiData.growthTrend < 0
      ? 'down'
      : 'neutral'

  const growthRelativeChange = growthDebugSummary
    && growthDebugSummary.historicalDailyGain !== null
    && Number.isFinite(growthDebugSummary.historicalDailyGain)
    && Math.abs(growthDebugSummary.historicalDailyGain) > 0
    ? ((kpiData.growthRate - growthDebugSummary.historicalDailyGain) / Math.abs(growthDebugSummary.historicalDailyGain)) * 100
    : null

  const growthTrendDetail = growthRelativeChange !== null
    ? `(${growthRelativeChange >= 0 ? '+' : ''}${growthRelativeChange.toFixed(2)}% relative)`
    : undefined

  const buildContextSearchParams = useCallback((extras?: Record<string, string | undefined>) => {
    const params = new URLSearchParams()
    if (selectedFarm?.id) params.set('farmId', String(selectedFarm.id))
    if (selectedFarm?.name) params.set('farmName', String(selectedFarm.name))
    if (selectedProductionUnit?.id) params.set('productionUnitId', String(selectedProductionUnit.id))
    if (selectedProductionUnit?.name) params.set('productionUnitName', String(selectedProductionUnit.name))
    if (selectedLot?.id) params.set('lotId', String(selectedLot.id))
    if (selectedLot?.lotKey) params.set('lotKey', String(selectedLot.lotKey))
    if (extras) {
      Object.entries(extras).forEach(([key, value]) => {
        if (typeof value === 'string' && value.trim().length > 0) params.set(key, value)
      })
    }
    const query = params.toString()
    return query ? `?${query}` : ''
  }, [selectedFarm, selectedLot, selectedProductionUnit])

  const downloadReportPdf = useCallback(async () => {
    try {
      const reportBytes = await buildDashboardReportPdf({
        generatedAtIso: new Date().toISOString(),
        lastUpdatedLabel: refreshTime.toISOString(),
        contextLabel: selectorSummaryText,
        kpis: {
          totalFishStock,
          fishStockTrend,
          activeDailyFeedKg: Number(kpiData.dailyFeed),
          activeDailyFeedTrendPercent: Number(kpiData.dailyFeedTrend),
          actualDailyFeedKg: Number(kpiData.actualDailyFeed),
          actualDailyFeedTrendPercent: Number(kpiData.actualDailyFeedTrend),
          actualDailyFeedEvents: Number(kpiData.actualDailyFeedEvents),
          weeklyMortalityRatePercentLabel: mortalityRateDisplay,
          weeklyMortalityTrendValue: mortalityTrendValue,
          weeklyMortalityTrendDirection: mortalityTrendDirection,
          weeklyMortalityTrendDetail: mortalityTrendDetail,
          growthRateLabel: `${growthRateValue} g/day`,
          growthTrendValue,
          growthTrendDirection,
          growthTrendDetail,
          water: {
            ph: {
              current: waterQualityKpis.ph.current,
              previous: waterQualityKpis.ph.previous,
              trend: waterQualityKpis.ph.trend,
              matchedLots: waterQualityKpis.ph.matchedLots,
              totalLots: waterQualityKpis.ph.totalLots,
              unit: ''
            },
            dissolvedOxygen: {
              current: waterQualityKpis.dissolvedOxygen.current,
              previous: waterQualityKpis.dissolvedOxygen.previous,
              trend: waterQualityKpis.dissolvedOxygen.trend,
              matchedLots: waterQualityKpis.dissolvedOxygen.matchedLots,
              totalLots: waterQualityKpis.dissolvedOxygen.totalLots,
              unit: 'mg/L'
            },
            salinity: {
              current: waterQualityKpis.salinity.current,
              previous: waterQualityKpis.salinity.previous,
              trend: waterQualityKpis.salinity.trend,
              matchedLots: waterQualityKpis.salinity.matchedLots,
              totalLots: waterQualityKpis.salinity.totalLots,
              unit: 'ppt'
            },
            temperature: {
              current: waterQualityKpis.temp.current,
              previous: waterQualityKpis.temp.previous,
              trend: waterQualityKpis.temp.trend,
              matchedLots: waterQualityKpis.temp.matchedLots,
              totalLots: waterQualityKpis.temp.totalLots,
              unit: 'C'
            }
          },
          activeTasks: taskDashboardSummary ? taskDashboardSummary.totalActive : null
        },
        notes: {
          dataError: dataError ?? undefined
        }
      })

      // pdf-lib returns a Uint8Array whose backing buffer type can be broader than BlobPart expects.
      // Copy into a fresh Uint8Array to keep TypeScript and browsers happy.
      const safeBytes = Uint8Array.from(reportBytes)
      const blob = new Blob([safeBytes], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      const safeDate = farmToday()
      anchor.download = `sunday-dashboard-report-${safeDate}.pdf`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      // Give the browser time to start the download before revoking.
      window.setTimeout(() => {
        window.URL.revokeObjectURL(url)
      }, 1500)
    } catch (error) {
      console.error('[dashboard] failed to generate report PDF', error)
      const message = error instanceof Error ? error.message : t('home.reportPdf.error.unableToGenerate')
      setDataError(t('home.reportPdf.error.generateReportFailed', { message }))
    }
  }, [
    dataError,
    fishStockTrend,
    growthRateValue,
    growthTrendDetail,
    growthTrendDirection,
    growthTrendValue,
    kpiData.dailyFeed,
    kpiData.dailyFeedTrend,
    mortalityRateDisplay,
    mortalityTrendDetail,
    mortalityTrendDirection,
    mortalityTrendValue,
    refreshTime,
    selectorSummaryText,
    taskDashboardSummary,
    totalFishStock,
    t,
    waterQualityKpis
  ])

  const containerStyle: React.CSSProperties = {
    padding: '24px',
    fontFamily: 'Inter, system-ui, sans-serif',
    background: '#f9fafb',
    minHeight: '100vh'
  }

  const sectionStyle: React.CSSProperties = {
    marginBottom: '32px'
  }

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px'
  }

  const actionGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  }

  const errorBannerStyle: React.CSSProperties = {
    marginBottom: '24px',
    padding: '16px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '12px',
    color: '#991b1b'
  }

  const alertStyle: React.CSSProperties = {
    padding: '16px',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '12px',
    marginBottom: '24px'
  }

  const selectorContainerStyle: React.CSSProperties = {
    marginBottom: '24px'
  }

  const selectedFarmLabel = useMemo(() => {
    if (!selectedFarmId) return '—'
    const selected = farms.find((farm) => resolveFarmKey(farm) === selectedFarmId)
    return selected?.name ?? selectedFarmId
  }, [farms, selectedFarmId])

  const selectedProductionUnitLabel = useMemo(() => {
    if (!selectedProductionUnitId) return '—'
    if (selectedProductionUnitId === ALL_PRODUCTION_UNITS_ID) return t('home.selector.productionUnit.all')
    const selected = availableProductionUnits.find((unit) => resolveProductionUnitKey(unit) === selectedProductionUnitId)
    return selected?.name ?? selectedProductionUnitId
  }, [availableProductionUnits, selectedProductionUnitId, t])

  const selectedLotLabel = useMemo(() => {
    if (!selectedLotId) return '—'
    const selected = findLotByDashboardSelection(selectedLotId, availableLots)
    return selected?.lotKey || selected?.id || selectedLotId
  }, [availableLots, selectedLotId])

  const alertTitleStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e3a8a',
    marginBottom: '8px'
  }

  const alertTextStyle: React.CSSProperties = {
    fontSize: '13px',
    color: '#1d4ed8',
    lineHeight: '1.5'
  }

  return (
    <div style={containerStyle}>
      {dataError && (
        <div style={errorBannerStyle}>
          <strong style={{ display: 'block', marginBottom: '4px' }}>{t('home.error.dataRefreshFailed')}</strong>
          <span>{dataError}</span>
        </div>
      )}
      <div style={sectionStyle}>
        <h2 className="h2">{t('home.title')}</h2>
        <p className="lead">{t('home.subtitle')}</p>
      </div>

      <CurrentContextBar
        species={{ value: speciesPresetLabel }}
        fields={[
          { label: t('home.context.facility'), value: selectedFarmLabel },
          { label: t('home.context.productionUnit'), value: selectedProductionUnitLabel },
          { label: t('home.context.lot'), value: selectedLotLabel }
        ]}
      />

      <div style={selectorContainerStyle}>
        <ContextSelectorBar
          farm={{
            label: t('home.selector.facility.label'),
            value: selectedFarmId,
            onChange: (value) => {
              handleFarmChange(value)

              const selectedFarm = farms.find((farm) => resolveFarmKey(farm) === value)
              const farmSpeciesKey = resolveFarmProductionSpeciesKey(selectedFarm)
              if (farmSpeciesKey) {
                setSelectedSpeciesKey(farmSpeciesKey)
                persistSpeciesSelection(farmSpeciesKey)
              }
            },
            placeholder: farms.length === 0
              ? t('home.selector.facility.placeholder.none')
              : t('home.selector.facility.placeholder.select'),
            options: farms.map((farm, index) => {
              const optionKey = resolveFarmKey(farm)
              return {
                key: optionKey || farm.name || `farm-${index}`,
                value: optionKey,
                label: farm.name ?? t('home.selector.facility.unnamed')
              }
            })
          }}
          productionUnit={{
            label: t('home.selector.productionUnit.label'),
            value: selectedProductionUnitId,
            onChange: (value) => {
              handleProductionUnitChange(value)
            },
            disabled: !selectedFarmId || availableProductionUnits.length === 0,
            placeholder: !selectedFarmId
              ? t('home.selector.productionUnit.placeholder.selectFacilityFirst')
              : availableProductionUnits.length === 0
                ? t('home.selector.productionUnit.placeholder.noneForFacility')
                : t('home.selector.productionUnit.placeholder.select'),
            options: availableProductionUnits.map((unit, index) => {
              const optionKey = resolveProductionUnitKey(unit)
              return {
                key: optionKey || unit.name || `unit-${index}`,
                value: optionKey,
                label: unit.name ?? t('home.selector.productionUnit.unnamed')
              }
            })
            .concat([
              {
                key: ALL_PRODUCTION_UNITS_ID,
                value: ALL_PRODUCTION_UNITS_ID,
                label: t('home.selector.productionUnit.all')
              }
            ])
          }}
          lot={{
            label: t('home.selector.lot.label'),
            value: selectedLotId,
            onChange: (value) => handleLotChange(value),
            disabled: !selectedProductionUnitId || availableLots.length === 0,
            placeholder: !selectedProductionUnitId
              ? t('home.selector.lot.placeholder.selectUnitFirst')
              : availableLots.length === 0
                ? t('home.selector.lot.placeholder.noneForUnit')
                : t('home.selector.lot.placeholder.select'),
            options: availableLots.map((lot, index) => {
              const lotKey = resolveDashboardSelectionLotKey(lot)
              return {
                key: lotKey || lot.lotKey || `lot-${index}`,
                value: lotKey,
                label: lot.lotKey || `Lot ${lot.id}`
              }
            })
          }}
          summary={null}
        />
      </div>

      {/* Alerts */}
      <div
        style={alertStyle}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h4 style={alertTitleStyle}>{t('home.alert.title')}</h4>
          <a
            href="/tasks"
            style={{ fontSize: 12, color: '#1d4ed8', textDecoration: 'none', fontWeight: 600 }}
          >
            {t('home.alert.viewTasks')}
          </a>
        </div>
        {systemAlertTasks.length === 0 ? (
          // Compact on purpose: the header row above already has a "Ver
          // tareas" link, so an empty state doesn't need its own second,
          // redundant button below the message — that was doubling this
          // card's height for a state that has nothing to show.
          <p style={{ ...alertTextStyle, marginTop: 8, marginBottom: 0 }}>
            {taskDashboardSummaryError
              ? t('home.alert.error.unableToLoad')
              : t('home.alert.empty.noAlerts')}
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {systemAlertTasks.map((task) => (
              <div key={task.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13, color: '#7f1d1d' }}>{task.title}</strong>
                  {task.tags?.some((tag) => tag.trim().toLowerCase() === 'alert') ? (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#1e3a8a',
                        background: '#dbeafe',
                        border: '1px solid #bfdbfe',
                        borderRadius: 999,
                        padding: '2px 8px'
                      }}
                    >
                      {t('home.alert.badge.alertTagged')}
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#92400e',
                        background: '#fffbeb',
                        border: '1px solid #fed7aa',
                        borderRadius: 999,
                        padding: '2px 8px'
                      }}
                    >
                      {classifyTaskStatus(task).code === 'overdue'
                        ? t('home.alert.badge.overdue')
                        : t('home.alert.badge.dueSoon')}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 12, color: '#1d4ed8' }}>
                  {task.dueAt
                    ? t('home.alert.dueAt', { date: new Date(task.dueAt).toLocaleString() })
                    : t('home.alert.noDueDate')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Featured Panel widgets — a personal (per-user) selection, arranged
          in a fixed-height window so a long list never pushes the rest of
          /home out of reach; extra widgets scroll within it. */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <h3 style={{ ...sectionTitleStyle, marginBottom: 0 }}>
            {t('home.pinnedWidgets.sectionTitle')}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              type="button"
              onClick={() => setIsAddWidgetPickerOpen(true)}
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#1d4ed8',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 999,
                padding: '6px 12px',
                cursor: 'pointer'
              }}
            >
              {t('home.pinnedWidgets.addWidget')}
            </button>
            <a
              href="/dashboard-new"
              style={{ fontSize: 12, color: '#1d4ed8', textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {t('home.pinnedWidgets.viewFullPanel')}
            </a>
          </div>
        </div>
        <div style={{
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '2px solid #e5e7eb',
          maxHeight: 640,
          overflowY: 'auto',
          padding: pinnedWidgetEntries.length === 0 ? 0 : 12
        }}>
          {pinnedWidgetEntries.length === 0 ? (
            <div style={{ padding: '20px', color: '#6b7280' }}>
              {t('home.pinnedWidgets.empty')}
            </div>
          ) : (
            <ResponsiveGridLayout
              className={styles.widgetGrid}
              layouts={{ [GRID_BREAKPOINT_DESKTOP]: homeGridLayout }}
              breakpoints={GRID_BREAKPOINTS}
              cols={GRID_COLS}
              rowHeight={GRID_ROW_HEIGHT}
              margin={[14, 14]}
              containerPadding={[0, 0]}
              isDraggable={homeGridBreakpoint === GRID_BREAKPOINT_DESKTOP}
              isResizable={homeGridBreakpoint === GRID_BREAKPOINT_DESKTOP}
              draggableHandle={`.${styles.widgetDragHandle}`}
              onBreakpointChange={(next) => setHomeGridBreakpoint(next)}
              onDragStop={handleHomeGridInteractionStop}
              onResizeStop={handleHomeGridInteractionStop}
            >
              {pinnedWidgetEntries.map(({ widget }) => (
                <div key={widget.id}>
                  <PanelWidgetCard
                    widget={widget}
                    report={reportsById.get(widget.reportId)}
                    runState={pinnedWidgetRunStates[widget.reportId]}
                    draggable
                    actions={
                      <button
                        type="button"
                        onClick={() => handleRemoveWidgetFromHome(widget.id)}
                      >
                        {t('home.pinnedWidgets.remove')}
                      </button>
                    }
                  />
                </div>
              ))}
            </ResponsiveGridLayout>
          )}
        </div>
      </div>

      <Drawer
        open={isAddWidgetPickerOpen}
        onClose={() => setIsAddWidgetPickerOpen(false)}
        title={t('home.pinnedWidgets.addWidget')}
      >
        {availableWidgetsToAdd.length === 0 ? (
          <p className="lead">{t('home.pinnedWidgets.addWidget.empty')}</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
            {availableWidgetsToAdd.map((widget) => (
              <li
                key={widget.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap'
                }}
              >
                <div>
                  <div style={{ fontWeight: 700 }}>{widget.title}</div>
                  <div className="lead">
                    {reportsById.get(widget.reportId)?.name ?? widget.reportId}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-gradient"
                  onClick={() => handleAddWidgetToHome(widget.id)}
                >
                  {t('home.pinnedWidgets.addWidget.add')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Drawer>

      {/* Water Quality Monitoring */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          {t('home.water.sectionTitle')}
        </h3>
        <div style={gridStyle}>
          <KPICard
            title={t('home.water.ph.title')}
            value={waterQualityKpis.ph.matchedLots > 0 ? waterQualityKpis.ph.current.toFixed(2) : '—'}
            icon="water"
            variant="default"
            trend={waterQualityKpis.ph.matchedLots > 0 ? {
              value: Number((waterQualityKpis.ph.previous !== null ? waterQualityKpis.ph.trend : 0).toFixed(2)),
              direction: (waterQualityKpis.ph.previous !== null ? waterQualityKpis.ph.trend : 0) > 0
                ? 'up'
                : (waterQualityKpis.ph.previous !== null ? waterQualityKpis.ph.trend : 0) < 0
                  ? 'down'
                  : 'neutral',
              period: t('home.kpi.trend.vsPrevious7Days')
            } : undefined}
            tooltip={waterQualityKpis.ph.matchedLots > 0
              ? `${t('home.water.tooltip.rollup', { matched: waterQualityKpis.ph.matchedLots, total: waterQualityKpis.ph.totalLots })} ${waterQualityKpis.ph.previous !== null
                ? t('home.water.tooltip.trend', { trendText: `${waterQualityKpis.ph.trend > 0 ? '+' : ''}${waterQualityKpis.ph.trend.toFixed(2)}` })
                : t('home.water.tooltip.noPrevBaseline')}`
              : t('home.water.tooltip.none.ph')
            }
          />
          
          <KPICard
            title={t('home.water.do.title')}
            value={waterQualityKpis.dissolvedOxygen.matchedLots > 0 ? waterQualityKpis.dissolvedOxygen.current.toFixed(2) : '—'}
            unit="mg/L"
            icon="water"
            variant="default"
            trend={waterQualityKpis.dissolvedOxygen.matchedLots > 0 ? {
              value: Number((waterQualityKpis.dissolvedOxygen.previous !== null ? waterQualityKpis.dissolvedOxygen.trend : 0).toFixed(2)),
              direction: (waterQualityKpis.dissolvedOxygen.previous !== null ? waterQualityKpis.dissolvedOxygen.trend : 0) > 0
                ? 'up'
                : (waterQualityKpis.dissolvedOxygen.previous !== null ? waterQualityKpis.dissolvedOxygen.trend : 0) < 0
                  ? 'down'
                  : 'neutral',
              period: t('home.kpi.trend.vsPrevious7Days'),
              unit: 'mg/L'
            } : undefined}
            tooltip={waterQualityKpis.dissolvedOxygen.matchedLots > 0
              ? `${t('home.water.tooltip.rollup', { matched: waterQualityKpis.dissolvedOxygen.matchedLots, total: waterQualityKpis.dissolvedOxygen.totalLots })} ${waterQualityKpis.dissolvedOxygen.previous !== null
                ? t('home.water.tooltip.trend', { trendText: `${waterQualityKpis.dissolvedOxygen.trend > 0 ? '+' : ''}${waterQualityKpis.dissolvedOxygen.trend.toFixed(2)} mg/L` })
                : t('home.water.tooltip.noPrevBaseline')}`
              : t('home.water.tooltip.none.do')
            }
          />
          
          <KPICard
            title={t('home.water.salinity.title')}
            value={waterQualityKpis.salinity.matchedLots > 0 ? waterQualityKpis.salinity.current.toFixed(1) : '—'}
            unit="‰"
            icon="water"
            variant="default"
            trend={waterQualityKpis.salinity.matchedLots > 0 ? {
              value: Number((waterQualityKpis.salinity.previous !== null ? waterQualityKpis.salinity.trend : 0).toFixed(2)),
              direction: (waterQualityKpis.salinity.previous !== null ? waterQualityKpis.salinity.trend : 0) > 0
                ? 'up'
                : (waterQualityKpis.salinity.previous !== null ? waterQualityKpis.salinity.trend : 0) < 0
                  ? 'down'
                  : 'neutral',
              period: t('home.kpi.trend.vsPrevious7Days'),
              unit: '‰'
            } : undefined}
            tooltip={waterQualityKpis.salinity.matchedLots > 0
              ? `${t('home.water.tooltip.rollup', { matched: waterQualityKpis.salinity.matchedLots, total: waterQualityKpis.salinity.totalLots })} ${waterQualityKpis.salinity.previous !== null
                ? t('home.water.tooltip.trend', { trendText: `${waterQualityKpis.salinity.trend > 0 ? '+' : ''}${waterQualityKpis.salinity.trend.toFixed(2)}‰` })
                : t('home.water.tooltip.noPrevBaseline')}`
              : t('home.water.tooltip.none.salinity')
            }
          />
          
          <KPICard
            title={t('home.water.temp.title')}
            value={waterQualityKpis.temp.matchedLots > 0 ? waterQualityKpis.temp.current.toFixed(1) : '—'}
            unit="°C"
            icon="water"
            variant="default"
            trend={waterQualityKpis.temp.matchedLots > 0 ? {
              value: Number((waterQualityKpis.temp.previous !== null ? waterQualityKpis.temp.trend : 0).toFixed(2)),
              direction: (waterQualityKpis.temp.previous !== null ? waterQualityKpis.temp.trend : 0) > 0
                ? 'up'
                : (waterQualityKpis.temp.previous !== null ? waterQualityKpis.temp.trend : 0) < 0
                  ? 'down'
                  : 'neutral',
              period: t('home.kpi.trend.vsPrevious7Days'),
              unit: '°C'
            } : undefined}
            tooltip={waterQualityKpis.temp.matchedLots > 0
              ? `${t('home.water.tooltip.rollup', { matched: waterQualityKpis.temp.matchedLots, total: waterQualityKpis.temp.totalLots })} ${waterQualityKpis.temp.previous !== null
                ? t('home.water.tooltip.trend', { trendText: `${waterQualityKpis.temp.trend > 0 ? '+' : ''}${waterQualityKpis.temp.trend.toFixed(2)}°C` })
                : t('home.water.tooltip.noPrevBaseline')}`
              : t('home.water.tooltip.none.temp')
            }
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          {t('home.quickActions.sectionTitle')}
        </h3>
        <div style={actionGridStyle}>
          <GradientButton
            variant="primary"
            icon="feed"
            onClick={() => navigate(`/feed${buildContextSearchParams({ focusWeek: 'closest' })}`)}
          >
            {t('home.quickActions.scheduleFeeding')}
          </GradientButton>
          
          <GradientButton
            variant="info"
            icon="water"
            onClick={() => navigate(`/water${buildContextSearchParams({ focus: 'new' })}`)}
          >
            {t('home.quickActions.recordWaterTest')}
          </GradientButton>
          
          <GradientButton
            variant="success"
            icon="biomas"
            onClick={() => navigate(`/biomass${buildContextSearchParams({ focus: 'new' })}`)}
          >
            {stockCountLabel}
          </GradientButton>
          
          <GradientButton
            variant="warning"
            icon="bell"
            onClick={() => navigate(`/tasks${buildContextSearchParams({ focus: 'create' })}`)}
          >
            {t('home.quickActions.setTask')}
          </GradientButton>
          
          <GradientButton
            variant="secondary"
            icon="database"
            onClick={() => navigate('/edit-farm?focus=export')}
          >
            {t('home.quickActions.backupData')}
          </GradientButton>
          
          <GradientButton
            variant="info"
            icon="dashboard-chart"
            onClick={() => {
              void downloadReportPdf()
            }}
          >
            {t('home.quickActions.generateReport')}
          </GradientButton>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          {t('home.activity.sectionTitle')}
        </h3>
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px'
        }}>
          {recentActivityError ? (
            <div style={{ display: 'grid', gap: 6 }}>
              <p style={{ fontSize: '14px', color: '#374151', margin: 0 }}>
                —
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                {t('home.activity.error.unableToLoad')}
              </p>
            </div>
          ) : recentActivity.length === 0 ? (
            <div style={{ display: 'grid', gap: 6 }}>
              <p style={{ fontSize: '14px', color: '#374151', margin: 0 }}>
                —
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                {t('home.activity.empty.noActivity')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {recentActivity.map((event) => {
                const icon = event.source === 'task' ? ('tasks' as any) : ('database' as any)
                const timeLabel = event.createdAt ? new Date(event.createdAt).toLocaleString() : ''
                const countSuffix = event.count && event.count > 1 ? ` · ${event.count}x` : ''

                return (
                  <div
                    key={event.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px 0'
                    }}
                  >
                    <IconButton
                      icon={icon}
                      variant="ghost"
                      size="sm"
                    />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '14px', color: '#374151', margin: 0 }}>
                        {translateActivityText(event.title, t)}{countSuffix}
                      </p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                        {event.detail ? `${translateActivityText(event.detail, t)} · ${timeLabel}` : timeLabel}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
